import axios from "axios";
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  FiUploadCloud,
  FiSend,
  FiXCircle,
  FiSave,
  FiLink,
  FiPlus,
  FiChevronDown,
  FiUpload,
  FiRefreshCw,
  FiTrash2,
  FiVideo,
  FiPlay,
  FiFacebook,
} from "react-icons/fi";
import { MdClose } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClipLoader } from "react-spinners";
import { getAuth } from "firebase/auth";
import { ref, set, push, get, getDatabase, onValue } from "firebase/database";
import { db } from "./firebase/config";
import { useLocation, useNavigate } from "react-router-dom";
// import { confirmAlert } from "react-confirm-alert"; // Removed
// import "react-confirm-alert/src/react-confirm-alert.css"; // Removed
import { resizeImage, processMultipleImages } from "./utils/imageProcessor";
import PointWarning from "./components/PointWarning";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { S3Client } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import ReviewQueuePanel from "./components/ReviewQueuePanel";
import {
  DEFAULT_POST_LENGTH,
  DEFAULT_POST_STYLES,
  POST_LENGTHS,
  POST_STYLES,
} from "./constants/postSettings";
import PostLanguageSelector from "./components/PostLanguageSelector";
import ScheduleModeSelector from "./components/ScheduleModeSelector";
import FixedDateScheduleUI from "./components/FixedDateScheduleUI";
import { getLanguageName } from "./constants/languages";
import {
  IMAGE_STYLE_OPTIONS,
  DEFAULT_IMAGE_STYLE,
  ASPECT_RATIO_OPTIONS,
  DEFAULT_ASPECT_RATIO,
} from "./services/aiImageService";
import SettingTooltip, {
  CONTACT_INFO_TOOLTIP_TEXT,
  FANPAGE_INFO_TOOLTIP_TEXT,
} from "./components/SettingTooltip";
import { buildFacebookAutoPostSettings } from "./utils/facebookAutoPostSettings";
import {
  getSocialAccountKey,
  loadConnectedSocialAccounts,
} from "./utils/socialChannels";
import {
  buildProductImagesSocialPayload,
  getProductImageAccountLabel,
  getProductImageAccountPlaceholder,
  getProductImageAccountSelectLabel,
  getProductImageAccountSetupLabel,
  getProductImageSelectedAccounts,
  normalizeProductImagesPlatform,
} from "./utils/productImagesSocialSettings";
import {
  removeProductImageConfigWithCronIndex,
  saveProductImageConfigWithCronIndex,
} from "./utils/rtdbProductImagesCronIndex";

// Tạo component SortableMediaItem cho cả ảnh và video
const SortableMediaItem = ({
  id,
  index,
  mediaData,
  mediaType, // 'image' hoặc 'video'
  handleRemoveMedia,
  openMediaPreview,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    transition: {
      duration: 150,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto",
    opacity: isDragging ? 0.8 : 1,
    touchAction: "none",
  };

  const isVideo = mediaType === 'video';
  const isYouTube = isVideo && mediaData.type === 'youtube';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square ${isDragging ? "shadow-lg ring-2 ring-blue-400" : ""}`}
    >
      {/* Hiển thị media */}
      {isVideo ? (
        <div className="w-full h-full bg-gray-900 rounded-lg overflow-hidden relative">
          {isYouTube ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-800">
              <div className="text-center p-2">
                <FiPlay className="text-3xl text-red-500 mx-auto mb-1" />
                <p className="text-white text-xs truncate max-w-full px-2">
                  YouTube
                </p>
              </div>
            </div>
          ) : (
            <video
              src={mediaData.url}
              className="w-full h-full object-cover"
              muted
              preload="metadata"
            />
          )}
          {/* Video duration badge */}
          {mediaData.duration && (
            <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-0.5 rounded z-10">
              {Math.floor(mediaData.duration / 60)}:{String(Math.floor(mediaData.duration % 60)).padStart(2, "0")}
            </div>
          )}
        </div>
      ) : (
        <img
          src={mediaData.url}
          alt={`Selected ${id}`}
          className="w-full h-full object-contain bg-gray-100 rounded-lg"
        />
      )}

      {/* Handle để kéo thả */}
      <div
        {...attributes}
        {...listeners}
        className="absolute inset-0 cursor-grab active:cursor-grabbing touch-manipulation"
        style={{ touchAction: "none" }}
      ></div>

      {/* Badge số thứ tự + loại media */}
      <div className={`absolute top-0 left-0 ${isVideo ? 'bg-purple-500' : 'bg-blue-500'} text-white text-xs px-1.5 py-0.5 rounded-br-md z-10 pointer-events-auto flex items-center gap-1`}>
        <span>{index + 1}</span>
        {isVideo ? (
          <FiVideo className="h-3 w-3" />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16m-7 6h7"
            />
          </svg>
        )}
      </div>

      {/* Nút xóa */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleRemoveMedia(id, mediaType);
        }}
        className="absolute top-2 right-2 text-red-600 cursor-pointer z-10 bg-white bg-opacity-75 rounded-full p-1 hover:bg-opacity-100 pointer-events-auto"
      >
        <FiXCircle size={18} />
      </button>

      {/* Trạng thái đã đăng */}
      {mediaData.postStatus && (
        <div className="absolute bottom-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs z-10">
          Đã đăng
        </div>
      )}

      {/* Nút xem preview (chỉ cho ảnh) */}
      {!isVideo && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            openMediaPreview(id, 'image');
          }}
          className="absolute top-2 left-10 bg-gray-800 bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full z-10 pointer-events-auto"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fillRule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}

      {/* Play button cho video */}
      {isVideo && !isYouTube && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            openMediaPreview(id, 'video');
          }}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 text-purple-600 p-3 rounded-full z-10 pointer-events-auto shadow-lg"
        >
          <FiPlay size={24} />
        </button>
      )}
    </div>
  );
};

const s3Client = new S3Client({
  region: process.env.REACT_APP_AWS_REGION,
  credentials: {
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  },
  requestChecksumCalculation: "WHEN_REQUIRED",
  responseChecksumValidation: "WHEN_REQUIRED",
});

const AddListImagesScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Hỗ trợ cả location.state và URL parameters cho middle-click
  let existingProduct = location.state?.product || null;

  // Nếu không có trong state, kiểm tra URL parameters
  if (!existingProduct) {
    const searchParams = new URLSearchParams(location.search);
    const productParam = searchParams.get("product");
    if (productParam) {
      try {
        existingProduct = JSON.parse(decodeURIComponent(productParam));
      } catch (error) {
        console.error("Error parsing product data from URL:", error);
      }
    }
  }

  // Đảm bảo lấy đúng ID từ sản phẩm hiện có
  const [productId] = useState(existingProduct?.id || null);
  const [productName, setProductName] = useState(existingProduct?.name || "");
  const [productInfo, setProductInfo] = useState(existingProduct?.info || "");
  const [customInfo, setCustomInfo] = useState(
    existingProduct?.customInfo || ""
  );
  // Convert legacy postTime to postTimes array format if needed
  const initialPostTimes =
    existingProduct?.postTimes ||
    (existingProduct?.postTime ? [existingProduct.postTime] : ["08:00"]);
  const [selectedImages, setSelectedImages] = useState(
    existingProduct?.images ? existingProduct.images : {}
  );
  const [imageUrl, setImageUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [fanpages, setFanpages] = useState([]);
  const [platform, setPlatform] = useState(
    normalizeProductImagesPlatform(existingProduct?.platform)
  );
  const [selectedFanpages, setSelectedFanpages] = useState(
    getProductImageSelectedAccounts(existingProduct)
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [postLength, setPostLength] = useState(
    existingProduct?.postLength || DEFAULT_POST_LENGTH
  );
  const [postStyles, setPostStyles] = useState(
    existingProduct?.postStyles
      ? existingProduct.postStyles.split(", ")
      : DEFAULT_POST_STYLES
  );
  const [selectedTimes, setSelectedTimes] = useState(initialPostTimes);
  const [autoPostEnabled, setAutoPostEnabled] = useState(
    existingProduct ? (existingProduct.autoPostEnabled ?? false) : true
  );
  const [postMode, setPostMode] = useState(
    existingProduct?.postMode || "direct"
  );
  const [includeContactInfo, setIncludeContactInfo] = useState(
    existingProduct?.includeContactInfo || false
  );
  const [useFanpageInfo, setUseFanpageInfo] = useState(
    existingProduct?.useFanpageInfo || false
  );
  const [firstCommentEnabled, setFirstCommentEnabled] = useState(
    existingProduct?.firstCommentEnabled === true
  );
  const [defaultFirstCommentText, setDefaultFirstCommentText] = useState(
    existingProduct?.defaultFirstCommentText || ""
  );
  const [userMembership, setUserMembership] = useState(null);

  // Lấy thông tin membership
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      const db = getDatabase();
      const membershipRef = ref(db, `/users/${userId}/membership`);
      const unsubscribe = onValue(membershipRef, (snapshot) => {
        if (snapshot.exists()) {
          setUserMembership(snapshot.val());
        }
      });
      return () => unsubscribe();
    }
  }, []);

  const [previewImage, setPreviewImage] = useState(null);
  const [previewMediaType, setPreviewMediaType] = useState("image"); // 'image' or 'video'
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Thêm state cho URL bài post
  const [postUrl, setPostUrl] = useState("");

  // Thêm state mới cho các ngày trong tuần
  const [selectedDays, setSelectedDays] = useState(
    existingProduct?.selectedDays || {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: true,
    }
  );

  // State mới: mediaOrder gộp ảnh + video
  const [mediaOrder, setMediaOrder] = useState([]);

  // Thêm state để theo dõi userPoint
  const [userPoint, setUserPoint] = useState(100); // Giá trị mặc định, sẽ được cập nhật

  // Thêm state cho ngôn ngữ
  const [selectedLanguage, setSelectedLanguage] = useState(
    existingProduct?.selectedLanguage || "vi"
  );

  // States cho schedule mode và fixed date schedules
  const [scheduleMode, setScheduleMode] = useState(
    existingProduct?.scheduleMode || "weekly"
  );
  const [fixedDateSchedules, setFixedDateSchedules] = useState(
    existingProduct?.fixedDateSchedules || []
  );

  // States cho tính năng logo
  const [logoFile, setLogoFile] = useState(null);
  const [logoPosition, setLogoPosition] = useState("top-right");
  const [logoOpacity, setLogoOpacity] = useState(0.8);
  const [logoSize, setLogoSize] = useState(20); // percentage of image width
  const logoInputRef = useRef(null);

  // ========================================
  // VIDEO SUPPORT STATES
  // ========================================
  const [selectedVideos, setSelectedVideos] = useState(
    existingProduct?.videos ? existingProduct.videos : {}
  );
  const [videoUrl, setVideoUrl] = useState("");
  const [videoOrder, setVideoOrder] = useState(
    existingProduct?.videos ? Object.keys(existingProduct.videos) : []
  );
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const videoInputRef = useRef(null);
  
  // Video config constants
  const VIDEO_CONFIG = {
    MAX_SIZE_MB: 150, // Đồng bộ với PhotosPostGenerator
    MAX_DURATION_MINUTES: 15,
    SUPPORTED_TYPES: ["video/mp4", "video/webm", "video/quicktime", "video/mov", "video/avi", "video/mpeg", "video/x-msvideo"],
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isMediaCommentModalOpen, setIsMediaCommentModalOpen] = useState(false);
  const [activeCommentMediaId, setActiveCommentMediaId] = useState(null);

  // ========================================
  // AI IMAGE GENERATION STATES
  // ========================================
  const [aiImageEnabled, setAiImageEnabled] = useState(
    existingProduct?.aiImageEnabled || false
  );
  const [imageStyle, setImageStyle] = useState(
    existingProduct?.imageStyle || DEFAULT_IMAGE_STYLE
  );
  const [aspectRatio, setAspectRatio] = useState(
    existingProduct?.aspectRatio || DEFAULT_ASPECT_RATIO
  );
  const [customImagePrompt, setCustomImagePrompt] = useState(
    existingProduct?.customImagePrompt || ""
  );
  const [isImageStyleDropdownOpen, setIsImageStyleDropdownOpen] = useState(false);
  const imageStyleDropdownRef = useRef(null);

  // Load ngôn ngữ từ localStorage và Firebase
  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        // Thử load từ localStorage trước
        const savedLanguage = localStorage.getItem("selectedLanguage");

        // Load từ Firebase nếu có userId
        const userId = localStorage.getItem("userId");
        if (userId) {
          const db = getDatabase();
          const userSettingsRef = ref(db, `users/${userId}/settings`);
          const snapshot = await get(userSettingsRef);

          if (snapshot.exists()) {
            const settings = snapshot.val();
            if (settings.selectedLanguage) {
              setSelectedLanguage(settings.selectedLanguage);
            }

            // Load logo settings
            if (settings.logoPosition) {
              setLogoPosition(settings.logoPosition);
            }
            if (settings.logoOpacity !== undefined) {
              setLogoOpacity(settings.logoOpacity);
            }
            if (settings.logoSize) {
              setLogoSize(settings.logoSize);
            }
          }
        }

        // Fallback về localStorage nếu Firebase không có
        if (savedLanguage) {
          setSelectedLanguage(savedLanguage);
        }
      } catch (error) {
        console.error("Error loading user settings:", error);
      }
    };

    loadUserSettings();
  }, []);

  const saveUserSettings = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const settingsRef = ref(db, `users/${userId}/settings`);

      const settings = {
        selectedLanguage,
        logoPosition,
        logoOpacity,
        logoSize,
      };

      await set(settingsRef, settings);
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("userAccessToken") == null) {
      navigate("/");
      return;
    }

    // Lấy danh sách account theo platform bằng one-time read để tránh realtime downloads.
    const loadFanpagesFromFirebase = async () => {
      try {
        const db = getDatabase();
        const userId = localStorage.getItem("userId");
        const accountsFromFirebase = await loadConnectedSocialAccounts(
          db,
          userId,
          { platform }
        );

        if (accountsFromFirebase.length > 0) {
          console.log("Accounts được tải từ Firebase:", accountsFromFirebase);
          setFanpages(accountsFromFirebase);

          const savedAccounts = getProductImageSelectedAccounts(existingProduct);
          const savedKeys = new Set(savedAccounts.map(getSocialAccountKey));
          const restoredAccounts = accountsFromFirebase.filter((account) =>
            savedKeys.has(getSocialAccountKey(account))
          );

          if (restoredAccounts.length > 0) {
            setSelectedFanpages(restoredAccounts);
          } else if (accountsFromFirebase.length === 1 && !existingProduct) {
            setSelectedFanpages([accountsFromFirebase[0]]);
          } else if (!existingProduct) {
            setSelectedFanpages([]);
          }
        } else {
          setFanpages([]);
          setSelectedFanpages([]);
          console.error("Không tìm thấy account Facebook/Zalo trong Firebase");
        }
      } catch (error) {
        console.error("Lỗi khi tải accounts từ Firebase:", error);
        toast.error(
          "Có lỗi khi tải dữ liệu tài khoản. Vui lòng thử lại sau!",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    };

    loadFanpagesFromFirebase();
  }, [navigate, productId, platform]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        imageStyleDropdownRef.current &&
        !imageStyleDropdownRef.current.contains(event.target)
      ) {
        setIsImageStyleDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handlePlatformChange = (nextPlatform) => {
    if (existingProduct) {
      return;
    }

    const normalizedPlatform = normalizeProductImagesPlatform(nextPlatform);
    setPlatform(normalizedPlatform);
    setSelectedFanpages([]);
    setIsDropdownOpen(false);

    if (normalizedPlatform === "zalo") {
      setPostMode("direct");
      setFirstCommentEnabled(false);
      setDefaultFirstCommentText("");
      setIncludeContactInfo(false);
    }
  };

  const handleFanpageSelect = (fanpage) => {
    // Không đóng dropdown để cho phép chọn nhiều fanpage
    // setIsDropdownOpen(false);

    // Kiểm tra xem fanpage đã được chọn chưa
    const targetKey = getSocialAccountKey(fanpage);
    const isAlreadySelected = selectedFanpages.some(
      (fp) => getSocialAccountKey(fp) === targetKey
    );

    if (isAlreadySelected) {
      // Nếu đã chọn rồi thì bỏ chọn
      const updatedFanpages = selectedFanpages.filter(
        (fp) => getSocialAccountKey(fp) !== targetKey
      );
      setSelectedFanpages(updatedFanpages);
      console.log("Đã bỏ chọn account:", fanpage.name);
    } else {
      // Nếu chưa chọn thì thêm vào danh sách
      setSelectedFanpages([...selectedFanpages, fanpage]);
      console.log("Đã thêm account:", fanpage.name);

      if ((fanpage.platform || "facebook") === "zalo") {
        return;
      }

      // Lưu thông tin fanpage Facebook đã chọn vào localStorage để dễ truy cập
      try {
        const db = getDatabase();
        const userId = localStorage.getItem("userId");

        // Lấy thông tin fanpage từ Firebase nếu có
        const fanpageInfoRef = ref(
          db,
          `users/${userId}/fanpages/${fanpage.id}`
        );
        get(fanpageInfoRef)
          .then((snapshot) => {
            if (snapshot.exists()) {
              const fanpageInfo = snapshot.val();
              // Lưu vào localStorage để tiện sử dụng
              const storedFanpages = JSON.parse(
                localStorage.getItem("fanpages") || "{}"
              );
              storedFanpages[fanpage.id] = { ...fanpageInfo };
              localStorage.setItem("fanpages", JSON.stringify(storedFanpages));
            }
          })
          .catch((error) => {
            console.error("Lỗi khi đọc thông tin fanpage từ Firebase:", error);
          });
      } catch (error) {
        console.error("Lỗi khi lưu thông tin fanpage đã chọn:", error);
      }
    }
  };

  // Cập nhật useEffect để khởi tạo mediaOrder khi component mount hoặc existingProduct thay đổi
  useEffect(() => {
    if (existingProduct) {
      const allMedia = [];

      // Add images
      if (existingProduct.images) {
        Object.values(existingProduct.images).forEach(img => {
            allMedia.push({ id: img.id, position: img.position || 0, type: 'image' });
        });
      }

      // Add videos
      if (existingProduct.videos) {
         Object.values(existingProduct.videos).forEach(vid => {
            allMedia.push({ id: vid.id, position: vid.position || 0, type: 'video' });
        });
      }

      // Sort by position
      allMedia.sort((a, b) => a.position - b.position);

      // Set order
      setMediaOrder(allMedia.map(item => item.id));
    }
  }, [existingProduct]);

  // Thêm hàm xử lý khi kéo thả hoàn tất
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setMediaOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Cập nhật cấu hình sensors để hỗ trợ tốt hơn trên thiết bị di động
  const sensors = useSensors(
    useSensor(TouchSensor, {
      // Điều chỉnh cấu hình để làm việc tốt hơn trên thiết bị cảm ứng
      activationConstraint: {
        delay: 100, // Giảm độ trễ xuống để phản ứng nhanh hơn
        tolerance: 8, // Tăng khả năng chịu đựng di chuyển nhỏ
      },
    }),
    useSensor(PointerSensor, {
      // Cấu hình cho chuột và bút stylus
      activationConstraint: {
        distance: 5, // Giảm khoảng cách tối thiểu để kích hoạt kéo
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Hàm xử lý chung cho files (ảnh + video) 
  const processFiles = async (files) => {
    setIsUploading(true);
    const newMediaIds = [];

    try {
      // 1. Phân loại
      const imageFiles = files.filter(f => f.type.startsWith('image/'));
      let videoFiles = files.filter(f => f.type.startsWith('video/'));

      // Chặn video nếu chưa có gói Chuyên nghiệp
      if (videoFiles.length > 0 && userMembership < 2) {
        toast.error("Tải video lên chỉ dành cho gói Chuyên nghiệp trở lên. Vui lòng nâng cấp gói dịch vụ!", {
          position: "top-right",
          autoClose: 5000,
        });
        videoFiles = [];
      }

      if (imageFiles.length === 0 && videoFiles.length === 0) {
          toast.warning("Không tìm thấy file ảnh hoặc video hợp lệ!");
          return;
      }

      // 2. Xử lý ảnh (Resize & Add to State - chưa upload)
      if (imageFiles.length > 0) {
        const resizePromises = imageFiles.map(async (file) => {
          const resizedFile = await resizeImage(file);
          const imageId = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          return {
            id: imageId,
            data: {
              id: imageId,
              url: URL.createObjectURL(resizedFile),
              file: resizedFile,
              postStatus: false,
              firstCommentText: "",
            },
          };
        });

        const imageResults = await Promise.all(resizePromises);
        const newImages = {};
        
        imageResults.forEach(({ id, data }) => {
            newImages[id] = data;
            newMediaIds.push(id);
        });

        setSelectedImages(prev => ({ ...prev, ...newImages }));
      }

      // 3. Xử lý video (Validate & Upload ngay)
      if (videoFiles.length > 0) {
          for (const file of videoFiles) {
             try {
                 // Validate
                 const videoInfo = await validateVideoFile(file);
                 
                 // Upload
                 const videoId = `vid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                 const fileName = `videos/${videoId}_${file.name}`;

                 const parallelUploads3 = new Upload({
                   client: s3Client,
                   params: {
                     Bucket: process.env.REACT_APP_S3_BUCKET_NAME,
                     Key: fileName,
                     Body: file,
                     ContentType: file.type,
                     // ACL: 'public-read', // Uncomment if bucket allows public ACLs
                   },
                 });

                 parallelUploads3.on("httpUploadProgress", (progress) => {
                   console.log("Upload progress:", progress);
                 });

                 const result = await parallelUploads3.done();
                 const videoUrl = result.Location || `https://${process.env.REACT_APP_S3_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${fileName}`;

                 setSelectedVideos(prev => ({
                     ...prev,
                     [videoId]: {
                         id: videoId,
                         url: videoUrl,
                         duration: videoInfo.duration,
                         postStatus: false,
                         type: "upload",
                         firstCommentText: "",
                     }
                 }));
                 newMediaIds.push(videoId);

             } catch (err) {
                 console.error("Error uploading video:", err);
                 toast.error(`Lỗi video ${file.name}: ${err.message}`);
             }
          }
      }

      // 4. Update Order
      if (newMediaIds.length > 0) {
          setMediaOrder(prev => [...prev, ...newMediaIds]);
          toast.success(`Đã thêm ${newMediaIds.length} media thành công!`);
      }

    } catch (error) {
       console.error("Process files error:", error);
       toast.error("Có lỗi xảy ra khi xử lý file!");
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    await processFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer.files);
    await processFiles(files);
  };

  const handleUploadClick = useCallback(() => {
    fileInputRef.current.click();
  }, []);

  const handleRemoveMedia = useCallback((id, type) => {
      if (type === 'image') {
          setSelectedImages((prev) => {
              const newImages = { ...prev };
              delete newImages[id];
              return newImages;
          });
      } else {
          setSelectedVideos((prev) => {
              const newVideos = { ...prev };
              delete newVideos[id];
              return newVideos;
          });
      }
      setMediaOrder((prev) => prev.filter(itemId => itemId !== id));
  }, []);

  // Giữ lại handleRemoveImage để tương thích ngược nếu cần, nhưng chuyển hướng sang handleRemoveMedia
  const handleRemoveImage = useCallback((imageId) => {
      handleRemoveMedia(imageId, 'image');
  }, [handleRemoveMedia]);

  const handleMediaFirstCommentChange = useCallback((id, type, value) => {
    if (type === "image") {
      setSelectedImages((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          firstCommentText: value,
        },
      }));
      return;
    }

    setSelectedVideos((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        firstCommentText: value,
      },
    }));
  }, []);

  // Hàm thêm media từ URL (ảnh hoặc video)
  const handleAddMediaUrl = () => {
    if (!imageUrl.trim()) return;

    // Check YouTube
    const youtubeMatch = imageUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    
    // Check Video URL extension
    const isVideoUrl = imageUrl.match(/\.(mp4|webm|ogg|mov)$/i);

    if (youtubeMatch || isVideoUrl) {
        const videoId = `vid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const type = youtubeMatch ? 'youtube' : 'url';
        
        setSelectedVideos((prev) => ({
             ...prev,
             [videoId]: {
                 id: videoId,
                 url: imageUrl,
                 postStatus: false,
                 type: type,
                 duration: 0, 
                 firstCommentText: "",
             }
        }));
        setMediaOrder(prev => [...prev, videoId]);
        toast.success("Đã thêm video thành công!");
        setImageUrl("");
        return;
    }

    // Default: Treat as image
    const imageId = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    setSelectedImages((prevImages) => ({
      ...prevImages,
      [imageId]: {
        id: imageId,
        url: imageUrl,
        postStatus: false,
        firstCommentText: "",
      },
    }));

    // Thêm ID ảnh mới vào cuối thứ tự
    setMediaOrder((prevOrder) => [...prevOrder, imageId]);
    setImageUrl("");
  };

  // ========================================
  // VIDEO HANDLERS
  // ========================================
  
  // Validate video file
  // Validate video file with iOS Safari compatibility
  const validateVideoFile = (file) => {
    return new Promise((resolve, reject) => {
      // Check file size
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > VIDEO_CONFIG.MAX_SIZE_MB) {
        reject(new Error(`Video không được vượt quá ${VIDEO_CONFIG.MAX_SIZE_MB}MB! (Hiện tại: ${sizeMB.toFixed(1)}MB)`));
        return;
      }

      // Check file type - hỗ trợ iOS video/quicktime
      if (!VIDEO_CONFIG.SUPPORTED_TYPES.includes(file.type)) {
        reject(new Error(`Định dạng video không được hỗ trợ! Hỗ trợ: MP4, WebM, MOV, AVI`));
        return;
      }

      // Check duration with iOS Safari compatibility
      const video = document.createElement("video");
      video.preload = "metadata";
      video.playsInline = true; // Required for iOS
      video.muted = true; // Required for autoplay on mobile
      
      let resolved = false;
      
      const handleMetadata = () => {
        if (resolved) return;
        resolved = true;
        
        URL.revokeObjectURL(video.src);
        clearTimeout(timeoutId);
        
        const durationMinutes = video.duration / 60;
        
        if (durationMinutes > VIDEO_CONFIG.MAX_DURATION_MINUTES) {
          reject(new Error(`Video không được dài hơn ${VIDEO_CONFIG.MAX_DURATION_MINUTES} phút! (Hiện tại: ${durationMinutes.toFixed(1)} phút)`));
          return;
        }
        
        resolve({
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
        });
      };
      
      // iOS Safari sometimes fires canplaythrough before loadedmetadata
      video.onloadedmetadata = handleMetadata;
      video.oncanplaythrough = handleMetadata;
      
      video.onerror = () => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeoutId);
        URL.revokeObjectURL(video.src);
        reject(new Error("Không thể đọc thông tin video!"));
      };
      
      // Timeout fallback for iOS - resolve với duration ước tính từ file size
      const timeoutId = setTimeout(() => {
        if (resolved) return;
        resolved = true;
        URL.revokeObjectURL(video.src);
        console.warn("Video metadata timeout - using estimated duration for iOS");
        // Ước tính duration: ~2MB/giây cho video chất lượng tốt
        const estimatedDuration = (file.size / (2 * 1024 * 1024)) * 60; // giây
        resolve({
          duration: Math.min(estimatedDuration, VIDEO_CONFIG.MAX_DURATION_MINUTES * 60),
          width: 0,
          height: 0,
        });
      }, 10000); // 10 giây timeout
      
      video.src = URL.createObjectURL(file);
      // Trigger load for iOS
      video.load();
    });
  };

  // Handle video file upload
  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsVideoUploading(true);

    try {
      // Validate video
      const videoInfo = await validateVideoFile(file);
      console.log("Video info:", videoInfo);

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "AutoWork");
      formData.append("api_key", "238276161954221");
      formData.append("resource_type", "video");

      const response = await axios.post(
        "https://api.cloudinary.com/v1_1/test-upload-1/video/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            console.log(`Upload progress: ${percentCompleted}%`);
          },
        }
      );

      const videoId = `vid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      setSelectedVideos((prevVideos) => ({
        ...prevVideos,
        [videoId]: {
          id: videoId,
          url: response.data.secure_url,
          duration: videoInfo.duration,
          postStatus: false,
          type: "upload",
          firstCommentText: "",
        },
      }));

      setMediaOrder((prevOrder) => [...prevOrder, videoId]);

      toast.success("Đã upload video thành công!", {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Lỗi khi upload video:", error);
      toast.error(error.message || "Có lỗi xảy ra khi upload video!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsVideoUploading(false);
      // Reset input
      if (videoInputRef.current) {
        videoInputRef.current.value = "";
      }
    }
  };

  // Handle video upload button click
  const handleVideoUploadClick = useCallback(() => {
    videoInputRef.current.click();
  }, []);

  // Handle remove video
  // Handle remove video
  const handleRemoveVideo = useCallback((videoId) => {
      handleRemoveMedia(videoId, 'video');
  }, [handleRemoveMedia]);

  // Handle add video from URL
  const handleAddVideoUrl = () => {
    if (!videoUrl.trim()) return;

    try {
      new URL(videoUrl);
    } catch (e) {
      toast.error("URL video không hợp lệ!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Check if it's a YouTube URL
    const isYouTube = videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be");

    const videoId = `vid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    setSelectedVideos((prevVideos) => ({
      ...prevVideos,
      [videoId]: {
        id: videoId,
        url: videoUrl,
        postStatus: false,
        type: isYouTube ? "youtube" : "url",
        firstCommentText: "",
      },
    }));

    setVideoOrder((prevOrder) => [...prevOrder, videoId]);
    setVideoUrl("");

    toast.success(
      isYouTube ? "Đã thêm video YouTube!" : "Đã thêm video từ URL!",
      {
        position: "top-right",
        autoClose: 2000,
      }
    );
  };

  const handlePostStyleChange = (style) => {
    setPostStyles((prevStyles) =>
      prevStyles.includes(style)
        ? prevStyles.filter((s) => s !== style)
        : [...prevStyles, style]
    );
  };

  const handleDayChange = (day) => {
    setSelectedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    // Lưu vào localStorage để duy trì lựa chọn
    localStorage.setItem("selectedLanguage", language);

    // Lưu vào Firebase nếu có userId
    const userId = localStorage.getItem("userId");
    if (userId) {
      try {
        const db = getDatabase();
        const userRef = ref(db, `users/${userId}/settings`);
        set(userRef, {
          selectedLanguage: language,
        });
      } catch (error) {
        console.error("Error saving language to Firebase:", error);
      }
    }
  };

  // Fixed date schedule handlers
  const handleAddFixedDateSchedule = (schedule) => {
    const newSchedule = {
      ...schedule,
      id: `schedule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      completed: false,
    };

    setFixedDateSchedules([...fixedDateSchedules, newSchedule]);
    toast.success("Đã thêm lịch đăng mới!", {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const handleEditFixedDateSchedule = (index, updates) => {
    const updated = [...fixedDateSchedules];
    updated[index] = { ...updated[index], ...updates };
    setFixedDateSchedules(updated);
    toast.success("Đã cập nhật lịch đăng!", {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const handleDeleteFixedDateSchedule = (index) => {
    setFixedDateSchedules(fixedDateSchedules.filter((_, i) => i !== index));
    toast.success("Đã xóa lịch đăng!", {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const handleToggleScheduleCompleted = (index) => {
    const updated = [...fixedDateSchedules];
    updated[index] = {
      ...updated[index],
      completed: !updated[index].completed,
      completedAt: !updated[index].completed ? new Date().toISOString() : null,
    };
    setFixedDateSchedules(updated);
  };

  const handleSubmit = async () => {
    if (!productName) {
      toast.error("Vui lòng nhập tên sản phẩm!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (selectedFanpages.length === 0) {
      toast.error(
        platform === "zalo"
          ? "Vui lòng chọn ít nhất một Zalo OA!"
          : "Vui lòng chọn ít nhất một Fanpage!",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
      return;
    }

    setIsUploading(true);

    const processedImages = {};
    const processedVideos = {}; // New: Store videos with position
    const imagesToUpload = [];

    // Duyệt qua mediaOrder để xử lý và gán position cho cả ảnh và video
    for (let index = 0; index < mediaOrder.length; index++) {
      const mediaId = mediaOrder[index];
      
      // Check images
      if (selectedImages[mediaId]) {
          const imgData = selectedImages[mediaId];
          if (imgData.file) {
              imagesToUpload.push({
                  imageId: mediaId,
                  file: imgData.file,
                  position: index,
              });
          } else if (imgData.url) {
              processedImages[mediaId] = {
                  ...imgData,
                  position: index,
              };
          }
      } 
      // Check videos
      else if (selectedVideos[mediaId]) {
          const vidData = selectedVideos[mediaId];
          processedVideos[mediaId] = {
              ...vidData,
              position: index,
          };
      }
    }

    // Upload song song các ảnh mới (nếu có)
    if (imagesToUpload.length > 0) {
      try {
        const uploadPromises = imagesToUpload.map(
          async ({ imageId, file, position }) => {
            // Add logo to image if logo exists
            let processedFile = file;
            if (logoFile) {
              try {
                processedFile = await addLogoToImage(
                  file,
                  logoFile,
                  logoPosition,
                  logoOpacity,
                  logoSize
                );
              } catch (logoError) {
                console.error("Error adding logo to image:", logoError);
                // Continue with original file if logo processing fails
              }
            }

            const formData = new FormData();
            formData.append("file", processedFile);
            formData.append("upload_preset", "AutoWork");
            formData.append("api_key", "238276161954221");

            const response = await axios.post(
              "https://api.cloudinary.com/v1_1/test-upload-1/image/upload",
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            return {
              imageId,
              url: response.data.secure_url,
              position,
              firstCommentText:
                selectedImages[imageId]?.firstCommentText || "",
            };
          }
        );

        const uploadResults = await Promise.all(uploadPromises);

        // Thêm các ảnh đã upload vào processedImages
        uploadResults.forEach(
          ({ imageId, url, position, firstCommentText }) => {
          processedImages[imageId] = {
            id: imageId,
            url: url,
            position: position,
            postStatus: false,
            firstCommentText: firstCommentText || "",
          };
        }
        );
      } catch (error) {
        console.error("Failed to upload images:", error);
        toast.error("Không thể tải ảnh lên! Vui lòng thử lại.", {
          position: "top-right",
          autoClose: 3000,
        });
        setIsUploading(false);
        return;
      }
    }

    const userId = localStorage.getItem("userId");
    const effectivePostMode = platform === "zalo" ? "direct" : postMode;
    const effectiveIncludeContactInfo =
      platform === "facebook" ? includeContactInfo : false;
    const effectiveFirstCommentEnabled =
      platform === "facebook" ? firstCommentEnabled : false;
    const effectiveDefaultFirstCommentText =
      platform === "facebook" ? defaultFirstCommentText : "";

    const promptSettings = buildFacebookAutoPostSettings({
      selectedFanpages,
      postLength,
      postStyles,
      customInfo,
      includeContactInfo: effectiveIncludeContactInfo,
      useFanpageInfo,
      selectedLanguage,
      postMode: effectivePostMode,
    });
    const socialPayload = buildProductImagesSocialPayload({
      platform,
      selectedAccounts: selectedFanpages,
      postMode: promptSettings.postMode,
      includeContactInfo: promptSettings.includeContactInfo,
      useFanpageInfo: promptSettings.useFanpageInfo,
      firstCommentEnabled: effectiveFirstCommentEnabled,
      defaultFirstCommentText: effectiveDefaultFirstCommentText,
    });

    try {
      let productData;
      const now = Date.now();

      if (existingProduct) {
        productData = {
          id: existingProduct.id,
          name: productName,
          info: productInfo,
          customInfo: promptSettings.customInfo,
          images: processedImages,
          videos: processedVideos, // Store videos with correct position
          postLength: promptSettings.postLength,
          postStyles: promptSettings.postStyles,
          // Save both formats for backwards compatibility
          postTime: selectedTimes[0], // Keep legacy single time field
          postTimes: selectedTimes, // New array format
          autoPostEnabled: autoPostEnabled,
          ...socialPayload,
          selectedDays: selectedDays,
          selectedLanguage: promptSettings.selectedLanguage,
          promptProfileVersion: promptSettings.promptProfileVersion,
          // New fields for fixed date scheduling
          scheduleMode: scheduleMode,
          fixedDateSchedules:
            scheduleMode === "fixed-dates" ? fixedDateSchedules : [],
          // AI Image Generation settings
          aiImageEnabled: aiImageEnabled,
          imageStyle: imageStyle,
          aspectRatio: aspectRatio,
          customImagePrompt: customImagePrompt,
          updatedAt: now,
        };
      } else {
        const newProductRef = push(ref(db, `productImages/${userId}`));
        productData = {
          id: newProductRef.key,
          name: productName,
          info: productInfo,
          customInfo: promptSettings.customInfo,
          images: processedImages,
          videos: processedVideos, // Store videos with correct position
          postLength: promptSettings.postLength,
          postStyles: promptSettings.postStyles,
          postTimes: selectedTimes,
          autoPostEnabled: autoPostEnabled,
          ...socialPayload,
          selectedDays: selectedDays,
          selectedLanguage: promptSettings.selectedLanguage,
          promptProfileVersion: promptSettings.promptProfileVersion,
          // New fields for fixed date scheduling
          scheduleMode: scheduleMode,
          fixedDateSchedules:
            scheduleMode === "fixed-dates" ? fixedDateSchedules : [],
          // AI Image Generation settings
          aiImageEnabled: aiImageEnabled,
          imageStyle: imageStyle,
          aspectRatio: aspectRatio,
          customImagePrompt: customImagePrompt,
          updatedAt: now,
        };
      }

      await saveProductImageConfigWithCronIndex(
        db,
        userId,
        productData.id,
        productData
      );

      toast.success(
        existingProduct
          ? "Sản phẩm đã được cập nhật thành công!"
          : "Sản phẩm đã được thêm thành công!",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );

      if (!existingProduct) {
        setProductName("");
        setProductInfo("");
        setCustomInfo("");
        setSelectedImages({});
        setSelectedVideos({});
        setMediaOrder([]);
        setSelectedFanpages([]);
      }
    } catch (error) {
      console.error("Failed to save product:", error);
      toast.error("Failed to save product!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Function thêm logo vào ảnh
  const addLogoToImage = async (
    imageFile,
    logoFile,
    position,
    opacity,
    sizePercent
  ) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Set canvas size to match image
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw original image
        ctx.drawImage(img, 0, 0);

        // If no logo, return original image
        if (!logoFile) {
          canvas.toBlob(resolve, "image/jpeg", 0.9);
          return;
        }

        const logoImg = new Image();
        logoImg.onload = () => {
          // Calculate logo size (percentage of image width)
          const logoWidth = (img.width * sizePercent) / 100;
          const logoHeight = (logoImg.height * logoWidth) / logoImg.width;

          // Calculate logo position
          let x, y;
          const padding = 20; // padding from edges

          switch (position) {
            case "top-left":
              x = padding;
              y = padding;
              break;
            case "top-right":
              x = img.width - logoWidth - padding;
              y = padding;
              break;
            case "center":
              x = (img.width - logoWidth) / 2;
              y = (img.height - logoHeight) / 2;
              break;
            case "bottom-left":
              x = padding;
              y = img.height - logoHeight - padding;
              break;
            case "bottom-right":
              x = img.width - logoWidth - padding;
              y = img.height - logoHeight - padding;
              break;
            default:
              x = img.width - logoWidth - padding;
              y = padding;
          }

          // Draw logo with opacity
          ctx.globalAlpha = opacity;
          ctx.drawImage(logoImg, x, y, logoWidth, logoHeight);
          ctx.globalAlpha = 1; // Reset alpha

          // Convert to blob
          canvas.toBlob(resolve, "image/jpeg", 0.9);
        };

        logoImg.onerror = () => {
          reject(new Error("Không thể tải logo"));
        };

        logoImg.src = URL.createObjectURL(logoFile);
      };

      img.onerror = () => {
        reject(new Error("Không thể tải ảnh"));
      };

      img.src = URL.createObjectURL(imageFile);
    });
  };

  // Handlers cho logo
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Kiểm tra kích thước file (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Logo không được vượt quá 5MB!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Kiểm tra định dạng file
      if (!file.type.startsWith("image/")) {
        toast.error("Vui lòng chọn file ảnh cho logo!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      setLogoFile(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const handleLogoUploadClick = () => {
    logoInputRef.current.click();
  };

  const handleLogoPositionChange = (position) => {
    setLogoPosition(position);
    saveUserSettings();
  };

  const handleLogoOpacityChange = (opacity) => {
    setLogoOpacity(opacity);
    saveUserSettings();
  };

  const handleLogoSizeChange = (size) => {
    setLogoSize(size);
    saveUserSettings();
  };

  const handleCreateNewFromCurrent = async () => {
    if (!productName || selectedFanpages.length === 0) {
      toast.error(
        platform === "zalo"
          ? "Vui lòng nhập tên sản phẩm và chọn Zalo OA!"
          : "Vui lòng nhập tên sản phẩm và chọn Fanpage!",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
      return;
    }

    setIsUploading(true);

    try {
      // Chuẩn bị dữ liệu media với position, reset trạng thái đăng cho bản sao.
      const processedImages = {};
      const processedVideos = {};
      const imagesToUpload = [];

      for (let index = 0; index < mediaOrder.length; index++) {
        const mediaId = mediaOrder[index];
        const imageData = selectedImages[mediaId];
        const videoData = selectedVideos[mediaId];

        if (imageData?.file) {
          // Ảnh mới cần upload
          imagesToUpload.push({
            imageId: mediaId,
            file: imageData.file,
            position: index,
          });
        } else if (imageData?.url) {
          // Ảnh đã có URL - reset postStatus về false cho bản sao mới
          processedImages[mediaId] = {
            id: mediaId,
            url: imageData.url,
            position: index,
            postStatus: false,
            firstCommentText: imageData.firstCommentText || "",
          };
        } else if (videoData?.url) {
          processedVideos[mediaId] = {
            ...videoData,
            id: mediaId,
            position: index,
            postStatus: false,
            firstCommentText: videoData.firstCommentText || "",
          };
        }
      }

      // Upload song song các ảnh mới (nếu có)
      if (imagesToUpload.length > 0) {
        const uploadPromises = imagesToUpload.map(
          async ({ imageId, file, position }) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("upload_preset", "AutoWork");
            formData.append("api_key", "238276161954221");

            const response = await axios.post(
              "https://api.cloudinary.com/v1_1/test-upload-1/image/upload",
              formData,
              {
                headers: { "Content-Type": "multipart/form-data" },
              }
            );

            return {
              imageId,
              url: response.data.secure_url,
              position,
              firstCommentText:
                selectedImages[imageId]?.firstCommentText || "",
            };
          }
        );

        const uploadResults = await Promise.all(uploadPromises);

        // Thêm các ảnh đã upload vào processedImages
        uploadResults.forEach(
          ({ imageId, url, position, firstCommentText }) => {
          processedImages[imageId] = {
            id: imageId,
            url: url,
            position: position,
            postStatus: false,
            firstCommentText: firstCommentText || "",
          };
        }
        );
      }

      // Create new product in database
      const userId = localStorage.getItem("userId");
      const newProductRef = push(ref(db, `productImages/${userId}`));

      // Reset fixed date schedules to pending if copying
      const copiedFixedDateSchedules =
        scheduleMode === "fixed-dates"
          ? fixedDateSchedules.map((schedule) => ({
              ...schedule,
              completed: false,
              completedAt: null,
              id: `schedule_${Date.now()}_${Math.random()
                .toString(36)
                .substr(2, 9)}`,
            }))
          : [];
      const effectivePostMode = platform === "zalo" ? "direct" : postMode;
      const effectiveIncludeContactInfo =
        platform === "facebook" ? includeContactInfo : false;
      const effectiveFirstCommentEnabled =
        platform === "facebook" ? firstCommentEnabled : false;
      const effectiveDefaultFirstCommentText =
        platform === "facebook" ? defaultFirstCommentText : "";
      const promptSettings = buildFacebookAutoPostSettings({
        selectedFanpages,
        postLength,
        postStyles,
        customInfo,
        includeContactInfo: effectiveIncludeContactInfo,
        useFanpageInfo,
        selectedLanguage,
        postMode: effectivePostMode,
      });
      const socialPayload = buildProductImagesSocialPayload({
        platform,
        selectedAccounts: selectedFanpages,
        postMode: promptSettings.postMode,
        includeContactInfo: promptSettings.includeContactInfo,
        useFanpageInfo: promptSettings.useFanpageInfo,
        firstCommentEnabled: effectiveFirstCommentEnabled,
        defaultFirstCommentText: effectiveDefaultFirstCommentText,
      });

      const productData = {
        id: newProductRef.key,
        name: productName,
        info: productInfo,
        customInfo: promptSettings.customInfo,
        images: processedImages,
        videos: processedVideos,
        postLength: promptSettings.postLength,
        postStyles: promptSettings.postStyles,
        postTimes: selectedTimes,
        autoPostEnabled: autoPostEnabled,
        ...socialPayload,
        selectedDays: selectedDays,
        selectedLanguage: promptSettings.selectedLanguage,
        promptProfileVersion: promptSettings.promptProfileVersion,
        // Copy schedule mode and fixed date schedules
        scheduleMode: scheduleMode,
        fixedDateSchedules: copiedFixedDateSchedules,
        aiImageEnabled: aiImageEnabled,
        imageStyle: imageStyle,
        aspectRatio: aspectRatio,
        customImagePrompt: customImagePrompt,
        updatedAt: Date.now(),
      };

      await saveProductImageConfigWithCronIndex(
        db,
        userId,
        newProductRef.key,
        productData
      );

      toast.success("Đã tạo sản phẩm mới thành công!", {
        position: "top-right",
        autoClose: 3000,
      });

      // Navigate to product manager first, then back to edit screen
      // This ensures proper state refresh
      navigate("/product-images-manager", {
        state: {
          shouldNavigateToEdit: true,
          productData: productData,
        },
      });
    } catch (error) {
      console.error("Failed to create new product:", error);
      toast.error("Không thể tạo sản phẩm mới!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteProduct = () => {
    setShowDeleteConfirm(true);
  };

  const executeDeleteProduct = async () => {
    setIsUploading(true);
    const userId = localStorage.getItem("userId");

    try {
      await removeProductImageConfigWithCronIndex(
        db,
        userId,
        existingProduct.id
      );

      toast.success("Đã xóa sản phẩm thành công!", {
        position: "top-right",
        autoClose: 3000,
      });

      // Đợi một chút để toast hiển thị
      setTimeout(() => {
        navigate("/product-images-manager");
      }, 1500);
    } catch (error) {
      console.error("Failed to delete product:", error);
      toast.error("Không thể xóa sản phẩm!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
      setShowDeleteConfirm(false);
    }
  };

  // Kiểm tra nếu có bất kỳ media nào đã được đăng
  const hasAnyMediaPosted = useMemo(() => {
    if (mediaOrder.length === 0) return false;
    return mediaOrder.some((id) => {
      if (selectedImages[id]) return selectedImages[id].postStatus === true;
      if (selectedVideos[id]) return selectedVideos[id].postStatus === true;
      return false;
    });
  }, [mediaOrder, selectedImages, selectedVideos]);

  const configuredMediaCommentCount = useMemo(() => {
    return mediaOrder.reduce((count, id) => {
      const mediaComment =
        selectedImages[id]?.firstCommentText || selectedVideos[id]?.firstCommentText || "";
      return mediaComment.trim() ? count + 1 : count;
    }, 0);
  }, [mediaOrder, selectedImages, selectedVideos]);

  const getMediaCommentEditorData = useCallback(
    (id) => {
      if (selectedImages[id]) {
        return {
          id,
          type: "image",
          data: selectedImages[id],
        };
      }

      if (selectedVideos[id]) {
        return {
          id,
          type: "video",
          data: selectedVideos[id],
        };
      }

      return null;
    },
    [selectedImages, selectedVideos]
  );

  const activeCommentMedia = useMemo(
    () => getMediaCommentEditorData(activeCommentMediaId),
    [activeCommentMediaId, getMediaCommentEditorData]
  );

  const activeCommentMediaIndex = useMemo(
    () => mediaOrder.findIndex((id) => id === activeCommentMediaId),
    [mediaOrder, activeCommentMediaId]
  );

  const openMediaCommentModal = useCallback(
    (mediaId = mediaOrder[0] || null) => {
      if (!mediaOrder.length) return;
      setActiveCommentMediaId(mediaId);
      setIsMediaCommentModalOpen(true);
    },
    [mediaOrder]
  );

  const closeMediaCommentModal = useCallback(() => {
    setIsMediaCommentModalOpen(false);
  }, []);

  const handleNavigateCommentMedia = useCallback(
    (direction) => {
      if (!mediaOrder.length || activeCommentMediaIndex === -1) return;

      const nextIndex =
        direction === "prev"
          ? (activeCommentMediaIndex - 1 + mediaOrder.length) % mediaOrder.length
          : (activeCommentMediaIndex + 1) % mediaOrder.length;

      setActiveCommentMediaId(mediaOrder[nextIndex]);
    },
    [mediaOrder, activeCommentMediaIndex]
  );

  useEffect(() => {
    if (!isMediaCommentModalOpen) return;

    if (!activeCommentMediaId || !mediaOrder.includes(activeCommentMediaId)) {
      setActiveCommentMediaId(mediaOrder[0] || null);
    }
  }, [isMediaCommentModalOpen, activeCommentMediaId, mediaOrder]);

  useEffect(() => {
    if (!isMediaCommentModalOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMediaCommentModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMediaCommentModalOpen]);

  // Thêm hàm xử lý reset postStatus cho cả images và videos
  const handleResetPostStatus = async () => {
    setIsUploading(true);
    const userId = localStorage.getItem("userId");

    try {
      // Reset postStatus cho images
      const resetImages = Object.entries(selectedImages).reduce(
        (acc, [key, value]) => {
          acc[key] = {
            ...value,
            postStatus: false,
          };
          return acc;
        },
        {}
      );

      // Reset postStatus cho videos
      const resetVideos = Object.entries(selectedVideos).reduce(
        (acc, [key, value]) => {
          acc[key] = {
            ...value,
            postStatus: false,
          };
          return acc;
        },
        {}
      );

      // Cập nhật trong state
      setSelectedImages(resetImages);
      setSelectedVideos(resetVideos);

      // Cập nhật trong database nếu sản phẩm đã tồn tại
      if (existingProduct) {
        const productRef = ref(
          db,
          `productImages/${userId}/${existingProduct.id}`
        );
        await set(productRef, {
          ...existingProduct,
          images: resetImages,
          videos: resetVideos,
        });
      }

      toast.success("Đã reset trạng thái đăng bài thành công!", {
        position: "top-right",
        autoClose: 3000,
      });
      setShowResetConfirm(false);
    } catch (error) {
      console.error("Failed to reset post status:", error);
      toast.error("Không thể reset trạng thái đăng bài!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const openMediaPreview = useCallback(
    (id, type = 'image') => {
      // Tìm index trong danh sách tổng hợp mediaOrder
      const index = mediaOrder.findIndex((itemId) => itemId === id);
      
      if (index !== -1) {
          setCurrentImageIndex(index);
          
          if (type === 'video' && selectedVideos[id]) {
             setPreviewImage(selectedVideos[id].url);
             setPreviewMediaType('video');
          } else if (selectedImages[id]) {
             setPreviewImage(selectedImages[id].url);
             setPreviewMediaType('image');
          }
      }
    },
    [mediaOrder, selectedImages, selectedVideos]
  );

  const closeImagePreview = useCallback(() => {
    setPreviewImage(null);
  }, []);

  const getMediaInfoAt = useCallback((index) => {
      const id = mediaOrder[index];
      if (selectedImages[id]) {
          return { url: selectedImages[id].url, type: 'image' };
      }
      if (selectedVideos[id]) {
          return { url: selectedVideos[id].url, type: 'video' };
      }
      return null;
  }, [mediaOrder, selectedImages, selectedVideos]);


  const handlePrevMedia = useCallback(
    (e) => {
      e.stopPropagation();
      const totalMedia = mediaOrder.length;
      if (totalMedia === 0) return;
      
      const newIndex = (currentImageIndex - 1 + totalMedia) % totalMedia;
      const mediaInfo = getMediaInfoAt(newIndex);
      
      if (mediaInfo) {
          setCurrentImageIndex(newIndex);
          setPreviewImage(mediaInfo.url);
          setPreviewMediaType(mediaInfo.type);
      }
    },
    [mediaOrder.length, currentImageIndex, getMediaInfoAt]
  );

  const handleNextMedia = useCallback(
    (e) => {
      e.stopPropagation();
      const totalMedia = mediaOrder.length;
      if (totalMedia === 0) return;

      const newIndex = (currentImageIndex + 1) % totalMedia;
      const mediaInfo = getMediaInfoAt(newIndex);

      if (mediaInfo) {
          setCurrentImageIndex(newIndex);
          setPreviewImage(mediaInfo.url);
          setPreviewMediaType(mediaInfo.type);
      }
    },
    [mediaOrder.length, currentImageIndex, getMediaInfoAt]
  );

  // Thêm hàm xử lý lấy ảnh từ bài post
  const handleGetImagesFromPost = async () => {
    if (platform !== "facebook") {
      toast.error("Chức năng lấy ảnh từ bài post chỉ hỗ trợ Facebook.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!postUrl || selectedFanpages.length === 0) {
      toast.error("Vui lòng nhập URL bài post và chọn ít nhất một Fanpage!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      // Lấy post ID từ URL
      let postId;
      let urlParams;

      if (postUrl.includes("permalink.php") || postUrl.includes("story.php")) {
        try {
          const url = new URL(postUrl);
          urlParams = new URLSearchParams(url.search);
          const storyFbid = urlParams.get("story_fbid");
          const pageId = urlParams.get("id");

          if (!storyFbid || !pageId) {
            throw new Error("URL không hợp lệ");
          }
          postId = storyFbid;
        } catch (error) {
          throw new Error("URL không hợp lệ");
        }
      } else {
        const postIdMatch = postUrl.match(/\/posts\/(\d+)/);
        if (!postIdMatch) {
          throw new Error("URL không hợp lệ");
        }
        postId = postIdMatch[1];
      }

      // Sử dụng fanpage đầu tiên trong danh sách đã chọn
      const firstSelectedFanpage = selectedFanpages[0];
      const pageId = firstSelectedFanpage.id;
      const pageAccessToken = firstSelectedFanpage.access_token;

      const response = await fetch(
        `https://graph.facebook.com/v21.0/${pageId}_${postId}?fields=id,message,attachments{subattachments{media}},created_time,permalink_url,full_picture&access_token=${pageAccessToken}`
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error.message);
      }

      // Lấy danh sách URL ảnh từ response
      let images = [];

      // Kiểm tra attachments trước
      if (data.attachments?.data[0]?.subattachments?.data) {
        images = data.attachments.data[0].subattachments.data.map(
          (item) => item.media.image.src
        );
      }

      // Nếu không có ảnh trong attachments, kiểm tra full_picture
      if (images.length === 0 && data.full_picture) {
        images = [data.full_picture];
      }

      if (images.length === 0) {
        toast.warning("Không tìm thấy ảnh trong bài post!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Thêm các ảnh vào selectedImages với định dạng phù hợp
      images.forEach((imageUrl) => {
        const imageId = `img_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        setSelectedImages((prevImages) => ({
          ...prevImages,
          [imageId]: {
            id: imageId,
            url: imageUrl,
            postStatus: false,
            firstCommentText: "",
          },
        }));

        // Thêm ID ảnh mới vào cuối thứ tự media tổng hợp
        setMediaOrder((prevOrder) => [...prevOrder, imageId]);
      });

      setPostUrl("");
      toast.success("Đã thêm ảnh từ bài post thành công!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error fetching post images:", error);
      toast.error("Không thể lấy ảnh từ bài post: " + error.message, {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Thêm UI cho chọn ngày trong tuần - thêm vào sau phần chọn thời gian
  const daysInVietnamese = {
    monday: "Thứ 2",
    tuesday: "Thứ 3",
    wednesday: "Thứ 4",
    thursday: "Thứ 5",
    friday: "Thứ 6",
    saturday: "Thứ 7",
    sunday: "Chủ nhật",
  };

  // Fetch user points
  useEffect(() => {
    const fetchUserPoints = async () => {
      try {
        const db = getDatabase();
        const userId = localStorage.getItem("userId");
        const pointsRef = ref(db, `users/${userId}/points`);
        const snapshot = await get(pointsRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          if (data.points !== undefined && data.purchasedPoints !== undefined) {
            // Cấu trúc mới
            setUserPoint(data.points + data.purchasedPoints);
          } else if (typeof data.points === "number") {
            // Cấu trúc cũ
            setUserPoint(data.points);
          } else {
            console.error("Invalid points value:", data);
            setUserPoint(0);
          }
        } else {
          setUserPoint(0);
        }
      } catch (error) {
        console.error("Error fetching user points:", error);
        setUserPoint(0);
      }
    };

    fetchUserPoints();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-5">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          {existingProduct
            ? "Chỉnh sửa AI viết & tự động đăng theo ảnh/video"
            : "AI viết & tự động đăng theo ảnh/video"}
        </h1>

        <PointWarning
          title="Vui lòng nạp thêm điểm để tiếp tục dùng chức năng này nhé ;)"
          userPoint={userPoint}
        />

        <div className="flex flex-col md:flex-row gap-6">
          {/* Phần trái - Form nhập thông tin */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-5 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Tải lên ảnh
                </h2>
                <a
                  href="https://youtu.be/AwHOIoCNTOY"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium rounded-md shadow-sm transition duration-300 hover:text-red-600"
                  title="Xem video hướng dẫn cách sử dụng"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-red-600"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                  </svg>
                  <span>Hướng dẫn</span>
                </a>
              </div>

              {/* Chọn nền tảng và tài khoản đăng bài */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Nền tảng đăng bài
                </label>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => handlePlatformChange("facebook")}
                    disabled={!!existingProduct}
                    className={`flex items-center justify-center gap-2 border rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      platform === "facebook"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                    } ${existingProduct ? "cursor-not-allowed opacity-70" : ""}`}
                  >
                    <FiFacebook className="h-4 w-4" />
                    Facebook
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePlatformChange("zalo")}
                    disabled={!!existingProduct}
                    className={`flex items-center justify-center gap-2 border rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      platform === "zalo"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                    } ${existingProduct ? "cursor-not-allowed opacity-70" : ""}`}
                  >
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-blue-600 text-[10px] font-bold text-white">
                      Z
                    </span>
                    Zalo OA
                  </button>
                </div>
                {existingProduct && (
                  <p className="mb-4 text-xs text-gray-500">
                    Không đổi nền tảng khi chỉnh sửa để tránh trộn trạng thái
                    đăng giữa các tài khoản.
                  </p>
                )}

                <label className="block text-gray-700 font-medium mb-2">
                  {getProductImageAccountSelectLabel(platform)}
                </label>
                {fanpages.length === 0 ? (
                  <button
                    onClick={() => {
                      const targetTab =
                        platform === "zalo" ? "zalo" : "facebook";
                      navigate("/profile", {
                        state: { activeTab: targetTab },
                      });
                      setTimeout(() => {
                        const element = document.getElementById(
                          platform === "zalo"
                            ? "zalo-account-management"
                            : "facebook-fanpage-management"
                        );
                        if (element) {
                          element.scrollIntoView({ behavior: "smooth" });
                        }
                      }, 500);
                    }}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base"
                  >
                    {getProductImageAccountSetupLabel(platform)}
                  </button>
                ) : (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={toggleDropdown}
                      type="button"
                      className="w-full flex justify-between items-center px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
                    >
                      <span>
                        {selectedFanpages.length > 0
                          ? selectedFanpages.length === 1
                            ? selectedFanpages[0].name
                            : `${selectedFanpages.length} tài khoản đã chọn`
                          : getProductImageAccountPlaceholder(platform)}
                      </span>
                      <FiChevronDown className="w-5 h-5 text-gray-400" />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
                        {/* Header với nút chọn/bỏ chọn tất cả */}
                        <div className="border-b border-gray-200 px-4 py-3 bg-gray-50">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-700 font-medium">
                              Chọn tài khoản ({selectedFanpages.length}/
                              {fanpages.length})
                            </span>
                            <button
                              onClick={() => {
                                if (
                                  selectedFanpages.length === fanpages.length
                                ) {
                                  // Nếu đã chọn tất cả thì bỏ chọn tất cả
                                  setSelectedFanpages([]);
                                } else {
                                  // Nếu chưa chọn tất cả thì chọn tất cả
                                  setSelectedFanpages([...fanpages]);
                                }
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
                            >
                              {selectedFanpages.length === fanpages.length
                                ? "Bỏ chọn tất cả"
                                : "Chọn tất cả"}
                            </button>
                          </div>
                        </div>

                        {/* Danh sách tài khoản */}
                        <div className="max-h-48 overflow-y-auto">
                          {fanpages.map((fanpage) => (
                            <div
                              key={getSocialAccountKey(fanpage)}
                              onClick={() => handleFanpageSelect(fanpage)}
                              className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center justify-between ${
                                selectedFanpages.find(
                                  (fp) =>
                                    getSocialAccountKey(fp) ===
                                    getSocialAccountKey(fanpage)
                                )
                                  ? "bg-blue-50"
                                  : ""
                              }`}
                            >
                              <div className="flex items-center flex-1">
                                <input
                                  type="checkbox"
                                  checked={selectedFanpages.some(
                                    (fp) =>
                                      getSocialAccountKey(fp) ===
                                      getSocialAccountKey(fanpage)
                                  )}
                                  onChange={() => {}}
                                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className="text-left truncate text-gray-700">
                                  {fanpage.name}
                                </span>
                                <span className="ml-2 shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                                  {getProductImageAccountLabel(fanpage)}
                                </span>
                              </div>
                              {selectedFanpages.find(
                                (fp) =>
                                  getSocialAccountKey(fp) ===
                                  getSocialAccountKey(fanpage)
                              ) && (
                                <svg
                                  className="h-5 w-5 text-blue-600"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Nút hoàn tất để đóng dropdown */}
                        <div className="border-t border-gray-200 px-4 py-3">
                          <button
                            onClick={() => setIsDropdownOpen(false)}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium"
                          >
                            Hoàn tất ({selectedFanpages.length} tài khoản đã
                            chọn)
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Hiển thị danh sách tài khoản đã chọn */}
                {selectedFanpages.length > 0 && (
                  <div className="mt-3">
                    <label className="block text-gray-700 font-medium mb-2">
                      Tài khoản đã chọn ({selectedFanpages.length}):
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {selectedFanpages.map((fanpage) => (
                        <div
                          key={getSocialAccountKey(fanpage)}
                          className="inline-flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full"
                        >
                          <span className="mr-2">
                            {fanpage.name} ({getProductImageAccountLabel(fanpage)})
                          </span>
                          <button
                            onClick={() => {
                              const updatedFanpages = selectedFanpages.filter(
                                (fp) =>
                                  getSocialAccountKey(fp) !==
                                  getSocialAccountKey(fanpage)
                              );
                              setSelectedFanpages(updatedFanpages);
                            }}
                            className="text-blue-600 hover:text-blue-800 focus:outline-none"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Zalo OA image guidelines */}
              {platform === "zalo" && selectedFanpages.length > 0 && (
                <div className="mt-2 p-3 border border-blue-100 rounded-md bg-blue-50">
                  <div className="flex items-start gap-2 text-xs text-blue-700">
                    <svg className="mt-0.5 shrink-0 w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span>
                      Zalo OA yêu cầu ảnh HTTPS public URL. Ảnh cover nên tỉ lệ 16:9 để hiển thị tốt nhất.
                    </span>
                  </div>
                </div>
              )}

              {/* Tên sản phẩm */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Tên sản phẩm/nhóm sản phẩm
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Nhập tên sản phẩm..."
                />
              </div>

              {/* Thông tin sản phẩm thực tế */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Thông tin sản phẩm / ngữ cảnh thực tế (không bắt buộc)
                </label>
                <div className="relative">
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    rows="4"
                    value={productInfo}
                    onChange={(e) => setProductInfo(e.target.value)}
                    placeholder="Nhập thông tin thật về sản phẩm hoặc bối cảnh sử dụng. Ví dụ: Chất liệu đã xác minh, màu sắc, kiểu dáng, nhóm khách hàng phù hợp, bối cảnh chụp, ưu điểm thực tế, lưu ý khi dùng..."
                  />
                  {productInfo.length > 0 && (
                    <div className="absolute right-2 bottom-2 text-xs text-gray-500 bg-white px-1.5 py-0.5 rounded-full border border-gray-200">
                      {productInfo.length} ký tự
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  💡 Mẹo: Chỉ nhập thông tin thật mà bạn muốn AI được phép dùng
                  làm facts hoặc chi tiết cụ thể trong bài viết
                </p>
              </div>

              {/* Yêu cầu cá nhân hóa nội dung */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Yêu cầu cá nhân hóa nội dung (không bắt buộc)
                </label>
                <div className="relative">
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    rows="4"
                    value={customInfo}
                    onChange={(e) => setCustomInfo(e.target.value)}
                    placeholder="Nhập yêu cầu để cá nhân hóa nội dung bài viết. Ví dụ: Xưng hô tôi/bạn, nhắm đến khách hàng nữ 25-35 tuổi, nhấn mạnh lợi ích sức khỏe, tránh dùng từ ngữ quá chuyên môn, thêm câu hỏi tương tác cuối bài..."
                  />
                  {customInfo.length > 0 && (
                    <div className="absolute right-2 bottom-2 text-xs text-gray-500 bg-white px-1.5 py-0.5 rounded-full border border-gray-200">
                      {customInfo.length} ký tự
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  💡 Mẹo: Mô tả về cách xưng hô, đối tượng khách hàng mục tiêu,
                  điểm nhấn nội dung, hoặc các yêu cầu đặc biệt khác
                </p>
              </div>

              {/* Khu vực kéo thả ảnh */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2 flex items-center gap-2">
                  <FiUploadCloud size={20} className="text-gray-600" />
                  <span className="inline-block ml-1">Tải ảnh hoặc video sản phẩm {userMembership < 2 && <span className="text-xs text-amber-600 ml-1">(🔒 Video: gói Chuyên nghiệp)</span>}</span>
                </label>

                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
                    ${
                      isUploading
                        ? "border-blue-300 bg-blue-50"
                        : "border-gray-300 hover:border-blue-400 hover:bg-blue-50/30"
                    }
                    ${mediaOrder.length > 0 ? "pb-24" : ""}
                  `}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={handleUploadClick}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    onClick={(e) => e.stopPropagation()}
                  />

                  {mediaOrder.length === 0 ? (
                    <>
                      <div className="mx-auto w-20 h-20 mb-4 flex items-center justify-center rounded-full bg-blue-50">
                        <FiUpload className="text-3xl text-blue-500" />
                      </div>
                      <p className="text-base text-gray-600 font-medium">
                        Kéo thả ảnh/video vào đây hoặc
                      </p>
                      <button
                        type="button"
                        className="mt-2 inline-flex items-center text-blue-600 font-medium hover:text-blue-700"
                      >
                        Chọn file từ máy tính
                      </button>
                      <p className="mt-2 text-sm text-gray-500">
                        Ảnh: JPG, PNG (max 5MB) | Video: MP4, MOV (max {VIDEO_CONFIG.MAX_SIZE_MB}MB, {VIDEO_CONFIG.MAX_DURATION_MINUTES} phút)
                      </p>
                    </>
                  ) : (
                    <div className="py-2">
                      {/* Hiển thị hướng dẫn thêm ảnh */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-center mb-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <div className="flex-1 flex flex-col justify-center items-center sm:items-start">
                          <p className="font-medium text-gray-700 mb-1 text-center sm:text-left">
                            Thêm ảnh mới
                          </p>
                          <p className="text-xs text-gray-500 text-center sm:text-left">
                            Nhấn nút hoặc kéo thả vào khu vực này
                          </p>
                        </div>
                        <div className="flex flex-row flex-wrap justify-center gap-2">
                          <button
                            type="button"
                            className="px-3 py-2 bg-white border border-gray-300 rounded text-gray-700 flex items-center gap-1 hover:bg-gray-50 text-sm"
                          >
                            <FiUpload className="text-gray-500" />
                            <span>Từ máy tính</span>
                          </button>
                          <button
                            type="button"
                            className="px-3 py-2 bg-white border border-gray-300 rounded text-gray-700 flex items-center gap-1 hover:bg-gray-50 text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              document
                                .getElementById("image-url-input")
                                .focus();
                            }}
                          >
                            <FiLink className="text-gray-500" />
                            <span>Từ URL</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ========================================
                  VIDEO UPLOAD SECTION
                  ======================================== */}


              {/* Cài đặt logo tự động */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <label className="text-gray-700 font-medium">
                    Logo tự động cho sản phẩm
                  </label>
                </div>

                {/* Upload logo */}
                <div className="mb-4">
                  <div className="flex items-center gap-3">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <button
                      onClick={handleLogoUploadClick}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      <FiUpload className="text-blue-600" />
                      <span className="text-sm font-medium">
                        {logoFile ? "Thay đổi logo" : "Tải lên logo"}
                      </span>
                    </button>

                    {logoFile && (
                      <button
                        onClick={handleRemoveLogo}
                        className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md hover:bg-red-100 transition-colors"
                      >
                        <FiXCircle className="text-red-600" />
                        <span className="text-sm font-medium">Xóa logo</span>
                      </button>
                    )}
                  </div>

                  {logoFile && (
                    <div className="mt-3 flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="w-12 h-12 bg-white border border-gray-200 rounded flex items-center justify-center overflow-hidden">
                        <img
                          src={URL.createObjectURL(logoFile)}
                          alt="Logo preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">
                          Logo đã được tải lên: {logoFile.name}
                        </p>
                        <p className="text-xs text-green-600">
                          Logo sẽ được tự động thêm vào tất cả ảnh sản phẩm khi
                          lưu
                        </p>
                      </div>
                    </div>
                  )}

                  {!logoFile && (
                    <p className="mt-2 text-xs text-gray-500">
                      Tải lên logo để tự động thêm vào ảnh sản phẩm khi lưu
                    </p>
                  )}
                </div>

                {/* Cài đặt vị trí và kích thước logo */}
                {logoFile && (
                  <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                    {/* Vị trí logo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Vị trí logo
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: "top-left", label: "Trên trái" },
                          { value: "top-right", label: "Trên phải" },
                          { value: "center", label: "Chính giữa" },
                          { value: "bottom-left", label: "Dưới trái" },
                          { value: "bottom-right", label: "Dưới phải" },
                        ].map((position) => (
                          <label
                            key={position.value}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="logoPosition"
                              value={position.value}
                              checked={logoPosition === position.value}
                              onChange={(e) =>
                                handleLogoPositionChange(e.target.value)
                              }
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">
                              {position.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Kích thước logo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kích thước logo: {logoSize}%
                      </label>
                      <input
                        type="range"
                        min="5"
                        max="50"
                        value={logoSize}
                        onChange={(e) =>
                          handleLogoSizeChange(Number(e.target.value))
                        }
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>5%</span>
                        <span>50%</span>
                      </div>
                    </div>

                    {/* Độ trong suốt */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Độ trong suốt: {Math.round(logoOpacity * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0.1"
                        max="1"
                        step="0.1"
                        value={logoOpacity}
                        onChange={(e) =>
                          handleLogoOpacityChange(Number(e.target.value))
                        }
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>10%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ========== AI IMAGE GENERATION SETTINGS ========== */}
              <div className={`mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 ${userMembership < 3 ? "opacity-60" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🤖</span>
                    <div>
                      <label className="block text-gray-800 font-semibold text-sm">
                        Tạo ảnh AI từ sản phẩm
                        {userMembership < 3 && (
                          <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded-full align-middle">
                            🔒 Cao Cấp
                          </span>
                        )}
                      </label>
                      <p className="text-xs text-gray-500">
                        AI sẽ giữ nguyên sản phẩm trong ảnh gốc và chỉ tạo lại bối cảnh phù hợp hơn để đăng bài
                      </p>
                    </div>
                  </div>
                  <label className={`relative inline-flex items-center ${userMembership < 3 ? "cursor-not-allowed pointer-events-none" : "cursor-pointer"}`}>
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={aiImageEnabled}
                      onChange={() => {
                        if (userMembership < 3) return;
                        setAiImageEnabled(!aiImageEnabled);
                      }}
                      disabled={userMembership < 3}
                    />
                    <div
                      className={`w-12 h-6 rounded-full transition-colors duration-200 ease-in-out
                        ${aiImageEnabled && userMembership >= 3 ? "bg-purple-500" : "bg-gray-300"}`}
                    >
                      <span
                        className={`block h-6 w-6 rounded-full shadow transform transition-transform duration-200 ease-in-out
                          ${
                            aiImageEnabled && userMembership >= 3
                              ? "translate-x-6 bg-white border-2 border-purple-500"
                              : "translate-x-0 bg-white"
                          }`}
                      />
                    </div>
                  </label>
                </div>
                {userMembership < 3 && (
                  <div className="mt-2 flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <span className="text-amber-600 text-sm">⬆️</span>
                    <p className="text-sm text-amber-700">
                      Nâng cấp lên gói <strong>Cao Cấp</strong> để sử dụng tính năng tạo ảnh bằng AI.
                    </p>
                  </div>
                )}

                {aiImageEnabled && (
                  <div className="mt-4 space-y-4">
                    {/* Image Style Dropdown */}
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        🎨 Phong cách ảnh
                      </label>
                      <div className="relative" ref={imageStyleDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsImageStyleDropdownOpen(!isImageStyleDropdownOpen)}
                          className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg flex items-center justify-between hover:border-purple-400 transition-colors text-sm"
                        >
                          <span>
                            {IMAGE_STYLE_OPTIONS.find(s => s.value === imageStyle)?.label || "Chọn phong cách"}
                          </span>
                          <FiChevronDown className={`transition-transform ${isImageStyleDropdownOpen ? "rotate-180" : ""}`} />
                        </button>
                        {isImageStyleDropdownOpen && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {IMAGE_STYLE_OPTIONS.map((style) => (
                              <button
                                key={style.value}
                                type="button"
                                onClick={() => {
                                  setImageStyle(style.value);
                                  setIsImageStyleDropdownOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-purple-50 transition-colors flex items-center justify-between ${
                                  imageStyle === style.value
                                    ? "bg-purple-50 text-purple-700 font-medium"
                                    : "text-gray-700"
                                }`}
                              >
                                <span>{style.label}</span>
                                {imageStyle === style.value && (
                                  <span className="text-purple-500">✓</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Aspect Ratio */}
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        📐 Tỷ lệ ảnh
                      </label>
                      <div className="flex gap-2">
                        {ASPECT_RATIO_OPTIONS.map((ratio) => (
                          <button
                            key={ratio.value}
                            type="button"
                            onClick={() => setAspectRatio(ratio.value)}
                            className={`flex-1 py-2 px-3 rounded-lg border-2 transition-all duration-200 text-xs font-medium text-center ${
                              aspectRatio === ratio.value
                                ? "border-purple-500 bg-purple-50 text-purple-700"
                                : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                            }`}
                          >
                            <div>{ratio.label}</div>
                            <div className="text-[10px] text-gray-400 mt-0.5">{ratio.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Prompt */}
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        ✍️ Yêu cầu thêm (tùy chọn)
                      </label>
                      <textarea
                        value={customImagePrompt}
                        onChange={(e) => setCustomImagePrompt(e.target.value)}
                        placeholder="VD: Nền trắng sạch, ánh sáng studio, thêm hoa trang trí..."
                        className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent resize-none"
                        rows={2}
                        maxLength={200}
                      />
                      <p className="text-xs text-gray-400 mt-1 text-right">
                        {customImagePrompt.length}/200
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Hiển thị danh sách ảnh/video đã tải lên */}
              {mediaOrder.length > 0 && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-gray-700 font-medium">
                      Danh sách media ({mediaOrder.length})
                    </label>
                    <div className="text-sm text-gray-500">
                      Kéo và thả để sắp xếp thứ tự
                    </div>
                  </div>

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    measuring={{
                      droppable: {
                        strategy: "always",
                      },
                    }}
                  >
                    <SortableContext
                      items={mediaOrder}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                        {mediaOrder.map((itemId, index) => {
                          let mediaData, mediaType;
                          if (selectedImages[itemId]) {
                            mediaData = selectedImages[itemId];
                            mediaType = 'image';
                          } else if (selectedVideos[itemId]) {
                            mediaData = selectedVideos[itemId];
                            mediaType = 'video';
                          }
                          
                          if (!mediaData) return null;

                          return (
                            <SortableMediaItem
                              key={itemId}
                              id={itemId}
                              index={index}
                              mediaData={mediaData}
                              mediaType={mediaType}
                              handleRemoveMedia={handleRemoveMedia}
                              openMediaPreview={openMediaPreview}
                            />
                          );
                        })}

                        <div
                            onClick={(e) => {
                              e.stopPropagation();
                              fileInputRef.current.click();
                            }}
                            className="aspect-square border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30"
                          >
                            <FiPlus className="text-2xl text-gray-400" />
                            <span className="text-xs text-gray-500 mt-1 text-center px-2">
                              Thêm
                            </span>
                          </div>
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {mediaOrder.length > 0 && (
                <div className="mb-6">
                  <div className="border border-gray-200 rounded-2xl p-4 sm:p-5 bg-gradient-to-br from-white to-slate-50">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <label className="block text-gray-800 font-semibold">
                            Comment đầu tiên theo từng media
                          </label>
                          <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                            Tùy chọn
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          Thiết lập comment riêng cho từng ảnh/video mà không làm
                          form quá dài. Comment riêng sẽ ưu tiên hơn comment chung.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-white border border-gray-200 text-sm text-gray-700">
                            {configuredMediaCommentCount}/{mediaOrder.length} media có comment riêng
                          </span>
                          {configuredMediaCommentCount > 0 ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm">
                              Đã sẵn sàng ưu tiên comment riêng
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-sm">
                              Chưa có comment riêng
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => openMediaCommentModal()}
                        className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
                      >
                        Thiết lập comment riêng
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Thêm ảnh từ URL */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.1901 8.7127L15.4701 10.9927M8.5601 13.3327L11.1901 15.9627M7.9201 8.2127H7.9301M18.0001 19.0027C19.6569 19.0027 21.0001 17.6596 21.0001 16.0027C21.0001 14.3459 19.6569 13.0027 18.0001 13.0027C16.3432 13.0027 15.0001 14.3459 15.0001 16.0027C15.0001 17.6596 16.3432 19.0027 18.0001 19.0027ZM6.0001 19.0027C7.65685 19.0027 9.0001 17.6596 9.0001 16.0027C9.0001 14.3459 7.65685 13.0027 6.0001 13.0027C4.34335 13.0027 3.0001 14.3459 3.0001 16.0027C3.0001 17.6596 4.34335 19.0027 6.0001 19.0027ZM12.0001 11.0027C13.6569 11.0027 15.0001 9.6596 15.0001 8.0027C15.0001 6.34585 13.6569 5.0027 12.0001 5.0027C10.3432 5.0027 9.0001 6.34585 9.0001 8.0027C9.0001 9.6596 10.3432 11.0027 12.0001 11.0027Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <label
                    className="text-gray-700 font-medium"
                    id="image-url-label"
                  >
                    Thêm ảnh từ URL
                  </label>
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiLink className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="image-url-input"
                    type="text"
                    className="w-full pl-10 pr-28 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="Nhập URL ảnh hoặc video YouTube/MP4..."
                    aria-labelledby="image-url-label"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleAddMediaUrl();
                      }}
                      disabled={!imageUrl.trim()}
                      className={`h-full px-4 rounded-r-md text-white font-medium transition-colors ${
                        !imageUrl.trim()
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-500 hover:bg-blue-600"
                      }`}
                    >
                      Thêm
                    </button>
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Nhập liên kết trực tiếp đến ảnh (URL phải kết thúc bằng .jpg,
                  .jpeg, .png)
                </p>
              </div>

              {/* Thêm ảnh từ bài post Facebook */}
              {platform === "facebook" && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 9.68586V12.9999M12 16.3139H12.01M6.4142 20.0001H17.5858C17.851 20.0001 18.1054 19.8947 18.2929 19.7072L19.7071 18.293C19.8946 18.1055 20 17.851 20 17.5859V6.41414C20 6.149 19.8946 5.89464 19.7071 5.70711L18.2929 4.29289C18.1054 4.10536 17.851 4 17.5858 4H6.4142C6.149 4 5.89464 4.10536 5.70711 4.29289L4.29289 5.70711C4.10536 5.89464 4 6.149 4 6.41414V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6.0001 20Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <label className="text-gray-700 font-medium">
                    Thêm ảnh từ bài post Facebook
                  </label>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="#1877F2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96C15.9164 21.5878 18.0622 20.3855 19.6099 18.57C21.1576 16.7546 22.0054 14.4456 22 12.06C22 6.53 17.5 2.04 12 2.04Z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="w-full pl-10 pr-28 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    value={postUrl}
                    onChange={(e) => setPostUrl(e.target.value)}
                    placeholder="https://www.facebook.com/..."
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      onClick={handleGetImagesFromPost}
                      disabled={
                        !postUrl.trim() ||
                        selectedFanpages.length === 0 ||
                        fanpages.length === 0
                      }
                      className={`h-full px-4 rounded-r-md text-white font-medium transition-colors ${
                        !postUrl.trim() ||
                        selectedFanpages.length === 0 ||
                        fanpages.length === 0
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-500 hover:bg-blue-600"
                      }`}
                    >
                      Lấy ảnh
                    </button>
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Nhập URL bài post để lấy ảnh (cần chọn ít nhất một Fanpage
                  trước)
                </p>
              </div>
              )}
            </div>
          </div>

          {/* Phần phải - Cấu hình đăng bài */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-5 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Cấu hình đăng bài
              </h2>

              {/* Chọn ngôn ngữ */}
              <div className="mb-6">
                <PostLanguageSelector
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={handleLanguageChange}
                />
              </div>

              {/* Chọn chế độ lịch đăng */}
              <ScheduleModeSelector
                value={scheduleMode}
                onChange={setScheduleMode}
                disabled={isUploading}
              />

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Độ dài bài viết
                </label>
                <div className="flex w-full rounded-md shadow-sm">
                  {POST_LENGTHS.map(({ value, label }, index) => (
                    <button
                      key={value}
                      type="button"
                      className={`flex-1 py-2 px-2 text-center text-sm font-medium
                        ${index === 0 ? "rounded-l-md" : ""}
                        ${
                          index === POST_LENGTHS.length - 1
                            ? "rounded-r-md"
                            : ""
                        }
                        ${
                          postLength === value
                            ? "bg-blue-600 text-white z-10"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      onClick={() => setPostLength(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Phong cách bài viết
                </label>
                <div className="flex flex-wrap gap-2">
                  {POST_STYLES.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`py-2 px-4 rounded-full text-sm font-medium 
                        ${
                          postStyles.includes(value)
                            ? "bg-blue-600 text-white"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                      onClick={() => handlePostStyleChange(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Conditional rendering based on schedule mode */}
              {scheduleMode === "weekly" ? (
                <div className="mb-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="w-full md:w-1/3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                      <label className="block text-gray-700 font-medium mb-2">
                        Giờ đăng bài
                      </label>
                      <div className="space-y-3">
                        {selectedTimes.map((time, index) => {
                          const [hours, minutes] = time.split(":");
                          return (
                            <div
                              key={index}
                              className="flex items-center gap-2"
                            >
                              <div className="relative flex-1 flex shadow-sm rounded-md overflow-hidden border border-gray-200">
                                {/* Dropdown giờ */}
                                <div className="relative w-1/2">
                                  <select
                                    value={hours}
                                    onChange={(e) => {
                                      const newTimes = [...selectedTimes];
                                      const newHours = e.target.value;
                                      newTimes[
                                        index
                                      ] = `${newHours}:${minutes}`;
                                      setSelectedTimes(newTimes);
                                    }}
                                    className="w-full h-full px-4 py-2.5 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium"
                                  >
                                    {Array.from({ length: 24 }, (_, i) => {
                                      const hour = i < 10 ? `0${i}` : `${i}`;
                                      return (
                                        <option key={hour} value={hour}>
                                          {hour}
                                        </option>
                                      );
                                    })}
                                  </select>
                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                    <svg
                                      className="w-4 h-4 fill-current"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                </div>

                                {/* Dấu hai chấm */}
                                <div className="flex items-center justify-center bg-gray-50 px-2">
                                  <span className="text-gray-500 font-medium">
                                    :
                                  </span>
                                </div>

                                {/* Dropdown phút */}
                                <div className="relative w-1/2">
                                  <select
                                    value={minutes}
                                    onChange={(e) => {
                                      const newTimes = [...selectedTimes];
                                      const newMinutes = e.target.value;
                                      newTimes[
                                        index
                                      ] = `${hours}:${newMinutes}`;
                                      setSelectedTimes(newTimes);
                                    }}
                                    className="w-full h-full px-4 py-2.5 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium"
                                  >
                                    {(() => {
                                      const isDev = process.env.NODE_ENV === 'development';
                                      const step = isDev ? 1 : 5;
                                      const count = isDev ? 60 : 12;
                                      return Array.from({ length: count }, (_, i) => {
                                        const minute = i * step;
                                        const formattedMinute =
                                          minute < 10
                                            ? `0${minute}`
                                            : `${minute}`;
                                        return (
                                          <option
                                            key={formattedMinute}
                                            value={formattedMinute}
                                          >
                                            {formattedMinute}
                                          </option>
                                        );
                                      });
                                    })()}
                                  </select>
                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                    <svg
                                      className="w-4 h-4 fill-current"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const newTimes = selectedTimes.filter(
                                    (_, i) => i !== index
                                  );
                                  setSelectedTimes(newTimes);
                                }}
                                className={`p-2 text-white ${
                                  selectedTimes.length > 1
                                    ? "bg-red-500 hover:bg-red-600"
                                    : "bg-gray-300 cursor-not-allowed"
                                } rounded-full transition-colors focus:outline-none ${
                                  selectedTimes.length > 1
                                    ? "focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                    : ""
                                }`}
                                aria-label="Xóa giờ đăng"
                                disabled={selectedTimes.length <= 1}
                                title={
                                  selectedTimes.length <= 1
                                    ? "Phải có ít nhất một giờ đăng bài"
                                    : "Xóa giờ đăng này"
                                }
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedTimes([...selectedTimes, "08:00"])
                          }
                          className="w-full px-4 py-2.5 bg-white text-blue-600 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center font-medium shadow-sm"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Thêm giờ đăng
                        </button>
                      </div>
                    </div>

                    <div className="w-full md:w-2/3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                      <label className="block text-gray-700 font-medium mb-2">
                        Ngày đăng bài
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(daysInVietnamese).map(
                          ([day, label]) => (
                            <label
                              key={day}
                              className={`inline-flex items-center justify-center px-3 py-2 rounded-md cursor-pointer select-none transition-colors duration-200
                              ${
                                selectedDays[day]
                                  ? "bg-blue-500 text-white"
                                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="hidden"
                                checked={selectedDays[day]}
                                onChange={() => handleDayChange(day)}
                              />
                              <span className="text-sm font-medium">
                                {label}
                              </span>
                            </label>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <FixedDateScheduleUI
                    schedules={fixedDateSchedules}
                    onAdd={handleAddFixedDateSchedule}
                    onEdit={handleEditFixedDateSchedule}
                    onDelete={handleDeleteFixedDateSchedule}
                    onToggleCompleted={handleToggleScheduleCompleted}
                  />
                </div>
              )}

              {/* Chế độ duyệt hiện chỉ áp dụng cho Facebook. */}
              {autoPostEnabled && platform === "facebook" && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block text-gray-700 font-medium">
                      Chế độ đăng bài
                    </label>
                    {userMembership < 2 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                        🔒 Chuyên nghiệp
                      </span>
                    )}
                  </div>
                  <div className={`flex gap-2 ${userMembership < 2 ? "opacity-50 pointer-events-none" : ""}`}>
                    <button
                      type="button"
                      onClick={() => setPostMode("direct")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                        postMode === "direct"
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      ⚡ Đăng ngay
                    </button>
                    <button
                      type="button"
                      onClick={() => setPostMode("review")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                        postMode === "review"
                          ? "border-amber-500 bg-amber-50 text-amber-700"
                          : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      👁 Duyệt trước
                    </button>
                  </div>
                  {userMembership < 2 ? (
                    <div className="mt-2 flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <span className="text-amber-600 text-sm">⬆️</span>
                      <p className="text-sm text-amber-700">
                        Nâng cấp lên gói <strong>Chuyên nghiệp</strong> để duyệt bài viết trước khi đăng.
                      </p>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">
                      {postMode === "direct"
                        ? `Bài viết sẽ được đăng trực tiếp lên ${platform === "zalo" ? "Zalo OA" : "Facebook"} ngay khi AI tạo xong.`
                        : "Bài viết sẽ được lưu vào hàng đợi để bạn duyệt trước khi đăng."}
                    </p>
                  )}
                </div>
              )}

              {selectedFanpages.length < 2 && (
                <div className="mb-6 border border-gray-200 rounded-xl p-4 bg-white">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <label className="block text-gray-700 font-medium">
                          {platform === "zalo"
                            ? "Dùng thông tin Zalo OA"
                            : "Dùng thông tin fanpage"}
                        </label>
                        <SettingTooltip
                          label={
                            platform === "zalo"
                              ? "Dùng thông tin Zalo OA"
                              : "Dùng thông tin fanpage"
                          }
                          text={FANPAGE_INFO_TOOLTIP_TEXT}
                        />
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={useFanpageInfo}
                        onChange={(e) => setUseFanpageInfo(e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 transition-colors" />
                      <div
                        className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                          useFanpageInfo ? "translate-x-5" : ""
                        }`}
                      />
                    </label>
                  </div>
                </div>
              )}

              {platform === "facebook" && (
              <div className="mb-6 border border-gray-200 rounded-xl p-4 bg-white">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <label className="block text-gray-700 font-medium">
                        Thêm thông tin liên hệ
                      </label>
                      <SettingTooltip
                        label="Thêm thông tin liên hệ"
                        text={CONTACT_INFO_TOOLTIP_TEXT}
                      />
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={includeContactInfo}
                      onChange={(e) =>
                        setIncludeContactInfo(e.target.checked)
                      }
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 transition-colors" />
                    <div
                      className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                        includeContactInfo ? "translate-x-5" : ""
                      }`}
                    />
                  </label>
                </div>
              </div>
              )}

              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <label className="block text-gray-700 font-medium">
                    Tự động viết và đăng bài
                  </label>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      name="autoPost"
                      id="autoPost"
                      checked={autoPostEnabled}
                      onChange={(e) => setAutoPostEnabled(e.target.checked)}
                      className="hidden"
                    />
                    <label
                      htmlFor="autoPost"
                      className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out
                        ${autoPostEnabled ? "bg-blue-500" : "bg-gray-300"}`}
                    >
                      <span
                        className={`block h-6 w-6 rounded-full shadow transform transition-transform duration-200 ease-in-out
                          ${
                            autoPostEnabled
                              ? "translate-x-6 bg-white border-2 border-blue-500"
                              : "translate-x-0 bg-white"
                          }`}
                      />
                    </label>
                  </div>
                </div>
                {autoPostEnabled ? (
                  <p className="mt-2 text-sm text-gray-600">
                    Hệ thống sẽ tự động viết và đăng bài theo thời gian đã cài
                    đặt
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">
                    Hiện tại tính năng tự động viết và đăng bài tự động đã tắt.
                    Vui lòng bật để sử dụng
                  </p>
                )}
              </div>

              {autoPostEnabled && (
                <div className="mb-6 border border-gray-200 rounded-xl p-4 bg-white">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div>
                      <label className="block text-gray-700 font-medium">
                        Tự động comment đầu tiên
                      </label>
                      <p className="text-sm text-gray-500 mt-1">
                        Sau khi bài đăng thành công, hệ thống sẽ comment link
                        hoặc nội dung bổ sung. Comment riêng của từng ảnh/video
                        sẽ được ưu tiên hơn comment chung.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={firstCommentEnabled}
                        onChange={(e) =>
                          setFirstCommentEnabled(e.target.checked)
                        }
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 transition-colors" />
                      <div
                        className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                          firstCommentEnabled ? "translate-x-5" : ""
                        }`}
                      />
                    </label>
                  </div>

                  <textarea
                    value={defaultFirstCommentText}
                    onChange={(e) =>
                      setDefaultFirstCommentText(e.target.value)
                    }
                    placeholder="Nhập comment đầu tiên mặc định cho toàn bộ ảnh/video..."
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none disabled:bg-gray-50 disabled:text-gray-400"
                    rows={3}
                    disabled={!firstCommentEnabled}
                  />

                  {firstCommentEnabled && selectedFanpages.filter(p => p.commentAutomationEligible !== true).length > 0 && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start text-xs text-amber-800">
                      <span className="text-amber-500 mr-2 mt-0.5">⚠️</span>
                      <div>
                        <p className="font-medium mb-1">Thiếu quyền bình luận cho Fanpage:</p>
                        <p className="mb-2">
                          Fanpage: <strong>
                            {selectedFanpages.filter(p => p.commentAutomationEligible !== true).map((p) => p.name).join(", ")}
                          </strong> hiện chưa được cấp quyền bình luận. Bình luận đầu tiên có thể không hoạt động.
                        </p>
                        <a
                          href="/profile"
                          className="text-blue-600 hover:text-blue-800 font-semibold underline"
                        >
                          Cấp thêm quyền tại trang thiết lập Fanpage
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Review Queue Panel */}
              <ReviewQueuePanel
                userId={localStorage.getItem("userId")}
                configId={existingProduct?.id}
                isVisible={
                  platform === "facebook" &&
                  postMode === "review" &&
                  !!existingProduct?.id &&
                  userMembership >= 2
                }
                basePath="productImages"
              />

              {/* Hiển thị các nút hành động chính */}
              <div className="flex items-center justify-center gap-2 py-2 px-3 mb-3 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-sm">💰</span>
                <p className="text-sm text-gray-600">
                  Chi phí dự kiến: <strong className={aiImageEnabled ? "text-purple-600" : "text-green-600"}>{aiImageEnabled ? "60" : "10"} điểm/bài</strong>
                  {firstCommentEnabled ? (
                    <span className="text-gray-500">
                      {" "}
                      + <strong className="text-blue-600">5 điểm/comment đầu tiên thành công</strong>
                    </span>
                  ) : null}
                </p>
              </div>
              {existingProduct ? (
                <div className="flex flex-row flex-wrap gap-3 mt-6">
                  {/* Nút Cập nhật sản phẩm */}
                  <button
                    onClick={handleSubmit}
                    disabled={isUploading || fanpages.length === 0}
                    className="flex-1 flex items-center justify-center bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                    title={
                      fanpages.length === 0
                        ? getProductImageAccountSetupLabel(platform)
                        : "Lưu các thay đổi đã chỉnh sửa"
                    }
                  >
                    {isUploading ? (
                      <ClipLoader size={20} color={"#fff"} className="mr-2" />
                    ) : (
                      <FiSave className="mr-2" />
                    )}
                    {isUploading ? "Đang lưu..." : "Lưu thay đổi"}
                  </button>

                  {/* Nút Tạo bản sao mới */}
                  <button
                    onClick={handleCreateNewFromCurrent}
                    disabled={isUploading || fanpages.length === 0}
                    className="flex-1 flex items-center justify-center bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                    title={
                      fanpages.length === 0
                        ? getProductImageAccountSetupLabel(platform)
                        : "Tạo một sản phẩm mới dựa trên sản phẩm hiện tại"
                    }
                  >
                    {isUploading ? (
                      <ClipLoader size={20} color={"#fff"} className="mr-2" />
                    ) : (
                      <FiPlus className="mr-2" />
                    )}
                    Tạo bản sao mới
                  </button>

                  {/* Nút Xóa sản phẩm */}
                  <button
                    onClick={handleDeleteProduct}
                    disabled={isUploading}
                    className="flex-1 flex items-center justify-center border border-red-600 text-red-600 py-2 px-4 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                    title="Xóa vĩnh viễn sản phẩm này và tất cả ảnh liên quan"
                  >
                    {isUploading ? (
                      <ClipLoader
                        size={20}
                        color={"#f87171"}
                        className="mr-2"
                      />
                    ) : (
                      <FiXCircle className="mr-2" />
                    )}
                    Xóa sản phẩm
                  </button>

                  {/* Nút Reset trạng thái đăng bài - hiện khi có ít nhất 1 media đã đăng */}
                  {hasAnyMediaPosted && (
                    <button
                      onClick={() => setShowResetConfirm(true)}
                      disabled={isUploading}
                      className="flex-1 flex items-center justify-center bg-amber-500 text-white py-2 px-4 rounded-md hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                      title="Đặt lại trạng thái đã đăng của tất cả ảnh/video"
                    >
                      {isUploading ? (
                        <ClipLoader size={20} color={"#fff"} className="mr-2" />
                      ) : (
                        <FiRefreshCw className="mr-2" />
                      )}
                      Reset trạng thái
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isUploading || fanpages.length === 0}
                  className="w-full flex items-center justify-center bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 transition-colors mt-6"
                  title={
                    fanpages.length === 0
                      ? getProductImageAccountSetupLabel(platform)
                      : "Tạo sản phẩm mới và lưu vào hệ thống"
                  }
                >
                  {isUploading ? (
                    <ClipLoader size={20} color={"#fff"} className="mr-3" />
                  ) : (
                    <FiSave className="mr-3" />
                  )}
                  <span className="font-medium text-base">
                    {isUploading ? "Đang tải lên..." : "Thêm sản phẩm"}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {isMediaCommentModalOpen && (
        <div
          className="fixed inset-0 z-[55] bg-black bg-opacity-50 flex items-center justify-center p-4"
          onClick={closeMediaCommentModal}
        >
          <div
            className="w-full max-w-6xl max-h-[88vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 sm:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Comment đầu tiên theo từng media
                  </h3>
                </div>
                <p className="text-sm text-gray-500">
                  Nếu ảnh hoặc video này có comment riêng thì hệ thống sẽ dùng
                  comment đó khi đăng bài. Nếu để trống, hệ thống sẽ dùng
                  comment chung đã thiết lập bên dưới.
                </p>
              </div>
              <button
                type="button"
                onClick={closeMediaCommentModal}
                className="self-start px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Xong
              </button>
            </div>

            <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
              <div className="lg:w-[320px] border-b lg:border-b-0 lg:border-r border-gray-200 bg-slate-50 overflow-y-auto p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-700">
                    Danh sách media
                  </p>
                  <span className="text-xs px-2 py-1 rounded-full bg-white border border-gray-200 text-gray-600">
                    {configuredMediaCommentCount}/{mediaOrder.length} đã cấu hình
                  </span>
                </div>

                <div className="space-y-2">
                  {mediaOrder.map((itemId, index) => {
                    const mediaItem = getMediaCommentEditorData(itemId);
                    if (!mediaItem) return null;

                    const hasCustomComment = Boolean(
                      (mediaItem.data.firstCommentText || "").trim()
                    );
                    const isActive = itemId === activeCommentMediaId;

                    return (
                      <button
                        key={`comment-media-list-${itemId}`}
                        type="button"
                        onClick={() => setActiveCommentMediaId(itemId)}
                        className={`w-full text-left rounded-2xl border p-3 transition-all ${
                          isActive
                            ? "border-blue-500 bg-white shadow-sm"
                            : "border-transparent bg-white/70 hover:border-gray-200"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center shrink-0">
                            {mediaItem.type === "image" ? (
                              <img
                                src={mediaItem.data.url}
                                alt={`Media ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="text-purple-600 flex flex-col items-center justify-center">
                                <FiVideo className="h-5 w-5 mb-1" />
                                <span className="text-[10px] font-medium">
                                  Video
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800">
                              #{index + 1}{" "}
                              {mediaItem.type === "image" ? "Ảnh" : "Video"}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {hasCustomComment
                                ? mediaItem.data.firstCommentText
                                : defaultFirstCommentText.trim()
                                ? defaultFirstCommentText
                                : "Chưa có comment mặc định"}
                            </p>
                            <span
                              className={`inline-flex mt-2 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                                hasCustomComment
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {hasCustomComment ? "Có comment riêng" : "Chưa thiết lập"}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6">
                {activeCommentMedia ? (
                  <div className="max-w-3xl">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                      <div>
                        <p className="text-sm text-gray-500">
                          Media {activeCommentMediaIndex + 1}/{mediaOrder.length}
                        </p>
                        <h4 className="text-lg font-semibold text-gray-900">
                          {activeCommentMedia.type === "image" ? "Ảnh" : "Video"} #{activeCommentMediaIndex + 1}
                        </h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleNavigateCommentMedia("prev")}
                          className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Trước
                        </button>
                        <button
                          type="button"
                          onClick={() => handleNavigateCommentMedia("next")}
                          className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Tiếp
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-[220px_minmax(0,1fr)] gap-5">
                      <div className="rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 min-h-[220px] flex items-center justify-center">
                        {activeCommentMedia.type === "image" ? (
                          <img
                            src={activeCommentMedia.data.url}
                            alt={`Media ${activeCommentMediaIndex + 1}`}
                            className="w-full h-full object-cover"
                          />
                        ) : activeCommentMedia.data.type !== "youtube" ? (
                          <video
                            src={activeCommentMedia.data.url}
                            controls
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-center p-4 text-purple-700">
                            <FiVideo className="h-8 w-8 mx-auto mb-2" />
                            <p className="text-sm font-medium">Video YouTube</p>
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Comment đầu tiên riêng
                          </p>
                          <p className="text-xs text-gray-500">
                            Nếu để trống, media này sẽ dùng comment chung của
                            thiết lập ở bên dưới màn hình.
                          </p>
                        </div>

                        <textarea
                          value={activeCommentMedia.data.firstCommentText || ""}
                          onChange={(e) =>
                            handleMediaFirstCommentChange(
                              activeCommentMedia.id,
                              activeCommentMedia.type,
                              e.target.value
                            )
                          }
                          placeholder="Nhập comment đầu tiên riêng cho media này..."
                          className="w-full border border-gray-300 rounded-2xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                          rows={7}
                        />

                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          <button
                            type="button"
                            onClick={() =>
                              handleMediaFirstCommentChange(
                                activeCommentMedia.id,
                                activeCommentMedia.type,
                                defaultFirstCommentText || ""
                              )
                            }
                            disabled={!defaultFirstCommentText.trim()}
                            className="px-3 py-2 rounded-xl border border-blue-200 text-blue-700 text-sm font-medium hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Điền từ comment chung
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleMediaFirstCommentChange(
                                activeCommentMedia.id,
                                activeCommentMedia.type,
                                ""
                              )
                            }
                            disabled={!(activeCommentMedia.data.firstCommentText || "").trim()}
                            className="px-3 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Xóa comment riêng
                          </button>
                        </div>

                        <div className="mt-4 rounded-2xl border border-gray-200 bg-slate-50 p-4">
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Đang ưu tiên
                          </p>
                          <p className="text-sm text-gray-600">
                            {(activeCommentMedia.data.firstCommentText || "").trim()
                              ? "Comment riêng của media này sẽ được dùng khi đăng bài."
                              : defaultFirstCommentText.trim()
                              ? "Media này sẽ dùng comment chung của thiết lập."
                              : "Chưa có comment nào được cấu hình cho media này."}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full min-h-[320px] flex items-center justify-center text-center text-gray-500">
                    Không có media nào để cấu hình comment.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
          onClick={closeImagePreview}
        >
          <div className="relative max-w-4xl w-full h-full flex items-center justify-center">
            {/* Previous button */}
            <button
              onClick={handlePrevMedia}
              className="absolute left-6 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full z-20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Close button */}
            <button
              onClick={closeImagePreview}
              className="absolute top-6 right-6 text-white hover:text-gray-300 z-20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Next button */}
            <button
              onClick={handleNextMedia}
              className="absolute right-6 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full z-20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Media Content */}
            {previewMediaType === 'video' ? (
                <video
                  src={previewImage}
                  controls
                  autoPlay
                  className="max-h-[90vh] max-w-full w-auto h-auto object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
            ) : (
                <img
                  src={previewImage}
                  alt="Preview"
                  className="max-h-[90vh] max-w-full w-auto h-auto object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
            )}

            {/* Image counter */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full z-20">
              {currentImageIndex + 1} / {mediaOrder.length}
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <FiTrash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Xác nhận xóa sản phẩm
                  </h3>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-500">
                  Bạn có chắc chắn muốn xóa sản phẩm này không?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Hành động này không thể hoàn tác và sẽ xóa tất cả dữ liệu liên quan.
                </p>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Hủy
                </button>
                <button
                  onClick={executeDeleteProduct}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <FiRefreshCw className="h-6 w-6 text-amber-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Xác nhận reset trạng thái
                  </h3>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-500">
                  Bạn có chắc chắn muốn reset trạng thái "Đã đăng" của tất cả ảnh/video không?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Điều này sẽ cho phép hệ thống đăng lại nội dung này một lần nữa.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Hủy
                </button>
                <button
                  onClick={handleResetPostStatus}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-500 border border-transparent rounded-md hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                >
                  Xác nhận Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default AddListImagesScreen;
